import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';
import { isWorkingDay as calIsWorkingDay, ResolvedCalendar } from './calendarEngine';
import { useResolvedCalendar } from '../hooks/useResolvedCalendar';
import { WORKING_CALENDAR_ENABLED } from '../config/featureFlags';
import WorkingCalendarModal from './WorkingCalendarModal';

export type { ResolvedCalendar } from './calendarEngine';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface Phase {
  id: string;
  title: string;
  description: string;
  color: string;
  order: number;
  start_day?: number;
  end_day?: number;
}

export type DepType = 'FS' | 'FF' | 'SS' | 'SF';

export interface DependencyLink {
  id: string;
  type: DepType;
  lag: number;
}

interface TimeEntry {
  id: string;
  date: string;
  md: number;
  team?: string;
  progress_pct?: number;
  note?: string;
}

export interface Activity {
  id: string;
  phase_id: string;
  wbs_code: string;
  title: string;
  description: string;
  type: 'activity' | 'milestone' | 'client_dependency';
  duration_days: number;
  start_day: number;
  end_day: number;
  dependencies: DependencyLink[];
  is_critical: boolean;
  is_milestone: boolean;
  assigned_to: string[];
  status: 'not_started' | 'in_progress' | 'completed' | 'delayed';
  progress_pct: number;
  time_entries?: TimeEntry[];
}

export interface ScheduleData {
  start_date: string;
  end_date: string;
  total_duration_days: number;
  phases: Phase[];
  activities: Activity[];
  milestones: Activity[];
}

const GANTT_LABEL_WIDTH = 450;
const DEFAULT_dayWidth = 26;
const PHASE_HEADER_H = 36;
const ACTIVITY_ROW_H = 40;

// ── Date ↔ day helpers ──────────────────────────────────────────────────────
// Parse YYYY-MM-DD as local midnight (not UTC midnight)
const parseDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const formatLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;

// `new Date(base.getTime() + n * 86400000)` is NOT a safe "add n calendar days" — across a
// DST transition (e.g. the last Sunday of October in Europe/Athens, where the local day is
// 25 hours long), raw millisecond arithmetic drifts the computed local calendar date by a
// full day for every date after the transition, until the next DST change happens to
// realign it. `setDate` operates on local calendar-date components directly, so it's immune.
const addCalendarDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const dayToDate = (projectStart: string, day: number): string => {
  if (!projectStart || !day) return '';
  const d = parseDate(projectStart);
  d.setDate(d.getDate() + (day - 1));
  return formatLocalDate(d);
};
const dateToDays = (projectStart: string, dateStr: string): number => {
  if (!projectStart || !dateStr) return 1;
  const diff = Math.round((parseDate(dateStr).getTime() - parseDate(projectStart).getTime()) / 86400000);
  return Math.max(1, diff + 1);
};

// ── Dependency helpers ───────────────────────────────────────────────────────
/** Normalize legacy string deps to DependencyLink (backward compat) */
export function normalizeDeps(raw: any[]): DependencyLink[] {
  return (raw || []).map(d =>
    typeof d === 'string' ? { id: d, type: 'FS' as DepType, lag: 0 } : { lag: 0, ...d },
  );
}

/**
 * Whether `day` (a 1-indexed offset from projectStart) is a working day. Calendar-aware
 * (week/exceptions/holidays) when `calendar` is provided; otherwise the exact legacy
 * Sat/Sun check — byte-identical fallback so every caller below still behaves exactly as
 * before when no project calendar is loaded (feature flag off, or no calendar_id set).
 */
function isWorkingDayLocal(day: number, projectStart: Date, calendar?: ResolvedCalendar): boolean {
  const date = addCalendarDays(projectStart, day - 1);
  if (calendar) return calIsWorkingDay(calendar, date);
  return date.getDay() !== 0 && date.getDay() !== 6;
}

/** Smallest working day >= day (skips non-working days forward). */
export function nextWorkingDay(day: number, projectStart: Date, calendar?: ResolvedCalendar): number {
  let d = day;
  while (!isWorkingDayLocal(d, projectStart, calendar)) d++;
  return d;
}

/**
 * N working days strictly after (n > 0) or before (n < 0) `day` — a non-working day in
 * between doesn't count towards n, it's just skipped over. n = 0 returns `day` unchanged.
 * This is how lead/lag are meant to be measured throughout the app (same convention as
 * activity duration), so non-working days never get silently "spent" as part of a lag.
 */
export function advanceWorkingDays(day: number, n: number, projectStart: Date, calendar?: ResolvedCalendar): number {
  if (n === 0) return day;
  let cur = day;
  let rem = Math.abs(n);
  const dir = n > 0 ? 1 : -1;
  while (rem > 0) {
    cur += dir;
    if (isWorkingDayLocal(cur, projectStart, calendar)) rem--;
  }
  return cur;
}

/** Required start_day of successor given ONE predecessor link. Lead/lag are counted in
 * working days when a project calendar is available (projectStart), falling back to plain
 * calendar-day arithmetic otherwise. */
export function requiredStartForLink(pred: Activity, succ: Activity, link: DependencyLink, projectStart?: Date, calendar?: ResolvedCalendar): number {
  const lag = link.lag ?? 0;
  if (!projectStart) {
    switch (link.type) {
      case 'FS': return pred.end_day + 1 + lag;
      case 'SS': return pred.start_day + lag;
      case 'FF': return pred.end_day + lag - succ.duration_days + 1;
      case 'SF': return pred.start_day + lag - succ.duration_days + 1;
    }
  }
  const ps = projectStart;
  switch (link.type) {
    case 'FS': {
      const base = nextWorkingDay(pred.end_day + 1, ps, calendar);
      return advanceWorkingDays(base, lag, ps, calendar);
    }
    case 'SS':
      return advanceWorkingDays(pred.start_day, lag, ps, calendar);
    case 'FF': {
      const targetEnd = advanceWorkingDays(pred.end_day, lag, ps, calendar);
      const succWorkDays = calToWork(succ.start_day, succ.end_day, ps, calendar);
      return subtractWorkingDays(targetEnd, succWorkDays, ps, calendar);
    }
    case 'SF': {
      const targetEnd = advanceWorkingDays(pred.start_day, lag, ps, calendar);
      const succWorkDays = calToWork(succ.start_day, succ.end_day, ps, calendar);
      return subtractWorkingDays(targetEnd, succWorkDays, ps, calendar);
    }
  }
}

/**
 * Single source of truth for "where must this activity move given its dependencies".
 * Used by BOTH the drag/resize cascade AND the edit-modal save — they used to have two
 * separate, diverging implementations: the modal's own copy never skipped weekends and
 * preserved calendar span instead of working-day duration, so a computed start landing on
 * a Sat/Sun silently ate into the activity's effective working-day length. Returns null if
 * no change is needed.
 */
export function computeConstrainedPosition(
  succ: Activity,
  links: DependencyLink[],
  findItem: (id: string) => Activity | undefined,
  projectStart?: Date,
  calendar?: ResolvedCalendar,
  // Overrides the internally-computed "working days to preserve" — needed when the
  // CALENDAR itself just changed (see recomputeScheduleForCalendar): calToWork(succ's
  // stale stored start/end, under the NEW calendar) is not the activity's real duration,
  // it's an unrelated number. Every other caller (single-activity edits, where the
  // calendar doesn't change mid-operation) leaves this undefined and keeps today's behavior.
  preserveWorkDays?: number,
  // When true, skips the "start_day unchanged -> null" shortcut below and always returns
  // the computed position. That shortcut assumes duration can only need to change when
  // start_day moves — true for a normal single-activity edit, but false when the CALENDAR
  // itself changed: a day inside an otherwise-unmoved [start_day, end_day] span can newly
  // become non-working, so end_day still needs to extend even though start_day is the
  // same. recomputeScheduleForCalendar sets this; every other caller leaves it false.
  forceRecompute = false,
): { start_day: number; end_day: number } | null {
  let requiredStart = Math.max(
    1,
    ...links.map(link => {
      const pred = findItem(link.id);
      return pred ? requiredStartForLink(pred, succ, link, projectStart, calendar) : 1;
    }),
  );

  // Advance to next working day (skip non-working days) — belt-and-suspenders in case any
  // branch above still returned one (e.g. the no-projectStart fallback never should here).
  if (projectStart) requiredStart = nextWorkingDay(requiredStart, projectStart, calendar);

  if (!forceRecompute && requiredStart === succ.start_day) return null;

  let newEnd: number;
  if (projectStart) {
    const workDays = preserveWorkDays ?? calToWork(succ.start_day, succ.end_day, projectStart, calendar);
    newEnd = addWorkingDays(requiredStart, workDays, projectStart, calendar);
  } else {
    newEnd = requiredStart + succ.duration_days - 1;
  }
  return { start_day: requiredStart, end_day: newEnd };
}

// ── Cascade (all dependency types) ───────────────────────────────────────────
export function cascadeFromActivity(changedId: string, data: ScheduleData, projectStart?: Date, calendar?: ResolvedCalendar): ScheduleData {
  const allA = [...data.activities, ...data.milestones];

  // Build: predecessorId → [successorIds]
  const succMap: Record<string, string[]> = {};
  allA.forEach(item => {
    normalizeDeps(item.dependencies).forEach(link => {
      if (!succMap[link.id]) succMap[link.id] = [];
      succMap[link.id].push(item.id);
    });
  });

  let acts = [...data.activities];
  let mils = [...data.milestones];

  const findItem = (id: string): Activity | undefined =>
    [...acts, ...mils].find(a => a.id === id);

  const patchItem = (id: string, startDay: number, endDay: number) => {
    const dur = endDay - startDay + 1;
    acts = acts.map(a => a.id === id ? { ...a, start_day: startDay, end_day: endDay, duration_days: dur } : a);
    mils = mils.map(m => m.id === id ? { ...m, start_day: startDay, end_day: endDay, duration_days: dur } : m);
  };

  const queue: string[] = [changedId];
  const seen = new Set<string>();

  while (queue.length) {
    const current = queue.shift()!;
    if (seen.has(current)) continue;
    seen.add(current);

    (succMap[current] || []).forEach(succId => {
      const succ = findItem(succId);
      if (!succ) return;

      const links = normalizeDeps(succ.dependencies);
      const pos = computeConstrainedPosition(succ, links, findItem, projectStart, calendar);
      if (pos) {
        patchItem(succId, pos.start_day, pos.end_day);
        queue.push(succId);
      }
    });
  }

  return { ...data, activities: acts, milestones: mils };
}

// ── Full topological recompute ────────────────────────────────────────────────
// Recalculates ALL start/end days from scratch using dependency graph.
// Root activities (no predecessors) keep their AI-assigned start_day.
// All others are pushed to respect constraints — eliminates arbitrary gaps.
function recomputeSchedule(data: ScheduleData): ScheduleData {
  const allA = [...data.activities, ...data.milestones];
  const inDegree: Record<string, number> = {};
  const succMap: Record<string, string[]> = {};
  allA.forEach(a => { inDegree[a.id] = 0; });
  allA.forEach(a => {
    normalizeDeps(a.dependencies).forEach(link => {
      if (!succMap[link.id]) succMap[link.id] = [];
      succMap[link.id].push(a.id);
      inDegree[a.id] = (inDegree[a.id] || 0) + 1;
    });
  });

  let acts = [...data.activities];
  let mils = [...data.milestones];
  const findItem = (id: string): Activity | undefined => [...acts, ...mils].find(a => a.id === id);
  const patchItem = (id: string, s: number, e: number) => {
    acts = acts.map(a => a.id === id ? { ...a, start_day: s, end_day: e } : a);
    mils = mils.map(m => m.id === id ? { ...m, start_day: s, end_day: e } : m);
  };

  // Process in topological order
  const queue = allA.filter(a => inDegree[a.id] === 0).map(a => a.id);
  const seen = new Set<string>();
  while (queue.length) {
    const cur = queue.shift()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    const item = findItem(cur);
    if (!item) continue;

    const links = normalizeDeps(item.dependencies);
    if (links.length > 0) {
      const req = Math.max(1, ...links.map(link => {
        const pred = findItem(link.id);
        return pred ? requiredStartForLink(pred, item, link) : 1;
      }));
      if (req !== item.start_day) {
        patchItem(cur, req, req + item.duration_days - 1);
      }
    }
    (succMap[cur] || []).forEach(sid => {
      inDegree[sid]--;
      if (inDegree[sid] === 0) queue.push(sid);
    });
  }

  // Recalculate total_duration_days and end_date from actual activity positions
  const allFinal = [...acts, ...mils];
  const maxDay = allFinal.reduce((m, a) => Math.max(m, a.end_day), 0);

  let newEndDate = data.end_date;
  if (data.start_date && maxDay > 0) {
    const start = parseDate(data.start_date);
    start.setDate(start.getDate() + maxDay - 1);
    newEndDate = formatLocalDate(start);
  }

  return { ...data, activities: acts, milestones: mils, total_duration_days: maxDay, end_date: newEndDate };
}

/**
 * Full topological recompute for a CALENDAR CHANGE (not a single-activity edit — see
 * cascadeFromActivity for that). When the project's calendar itself changes, every
 * activity's working-day duration needs to be re-preserved under the new calendar, not
 * just the ones downstream of one edited activity:
 * - Root activities (no dependencies): start_day is a fixed anchor, but end_day is
 *   re-derived from the SAME working-day duration_days under the new calendar (a holiday
 *   landing inside its span pushes the end date out, exactly like a dependency-constrained
 *   successor already does via computeConstrainedPosition — this just extends that same
 *   "preserve working-day duration" rule to activities with no predecessor).
 * - Dependency-constrained activities: computeConstrainedPosition already does this.
 * Must run in topological order (Kahn's algorithm) — NOT a per-item loop over the raw
 * activities array (that array isn't guaranteed to be in dependency order, so a naive
 * single forward pass over it can leave long chains half-propagated depending on
 * insertion order).
 */
export function recomputeScheduleForCalendar(data: ScheduleData, projectStart: Date, calendar?: ResolvedCalendar, oldCalendar?: ResolvedCalendar): ScheduleData {
  const allA = [...data.activities, ...data.milestones];
  const inDegree: Record<string, number> = {};
  const succMap: Record<string, string[]> = {};
  allA.forEach(a => { inDegree[a.id] = 0; });
  allA.forEach(a => {
    normalizeDeps(a.dependencies).forEach(link => {
      if (!succMap[link.id]) succMap[link.id] = [];
      succMap[link.id].push(a.id);
      inDegree[a.id] = (inDegree[a.id] || 0) + 1;
    });
  });

  let acts = [...data.activities];
  let mils = [...data.milestones];
  const findItem = (id: string): Activity | undefined => [...acts, ...mils].find(a => a.id === id);
  const patchItem = (id: string, s: number, e: number) => {
    const dur = e - s + 1;
    acts = acts.map(a => a.id === id ? { ...a, start_day: s, end_day: e, duration_days: dur } : a);
    mils = mils.map(m => m.id === id ? { ...m, start_day: s, end_day: e, duration_days: dur } : m);
  };

  const queue = allA.filter(a => inDegree[a.id] === 0).map(a => a.id);
  const seen = new Set<string>();
  while (queue.length) {
    const cur = queue.shift()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    const item = findItem(cur);
    if (!item) continue;

    // "Working days to preserve" must come from the OLD calendar applied to the item's
    // CURRENT stored [start_day, end_day] — that's its real duration right now. Using the
    // NEW calendar on the same stale range (or item.duration_days, a calendar-day span —
    // see cascadeFromActivity's patchItem) computes an unrelated number and can corrupt
    // items that were never even touched by the calendar change (confirmed live: a 5-day
    // activity ending well before a new exception's date still got dragged out to 9 days).
    const links = normalizeDeps(item.dependencies);
    if (links.length > 0) {
      const preserveWorkDays = oldCalendar ? calToWork(item.start_day, item.end_day, projectStart, oldCalendar) : undefined;
      const pos = computeConstrainedPosition(item, links, findItem, projectStart, calendar, preserveWorkDays, !!oldCalendar);
      if (pos && (pos.start_day !== item.start_day || pos.end_day !== item.end_day)) patchItem(cur, pos.start_day, pos.end_day);
    } else if (!item.is_milestone) {
      const workDays = Math.max(1, oldCalendar ? calToWork(item.start_day, item.end_day, projectStart, oldCalendar) : item.duration_days);
      const newEnd = addWorkingDays(item.start_day, workDays, projectStart, calendar);
      if (newEnd !== item.end_day) patchItem(cur, item.start_day, newEnd);
    }

    (succMap[cur] || []).forEach(sid => {
      inDegree[sid] = (inDegree[sid] || 1) - 1;
      if (inDegree[sid] === 0) queue.push(sid);
    });
  }

  // Recalculate total_duration_days and end_date from actual activity positions — a
  // calendar edit routinely shrinks or grows the project span (see cascadeFromActivity's
  // identical tail), and leaving these stale breaks anything derived from them downstream
  // (e.g. the live Gantt's resolved-calendar refetch range, which otherwise never notices
  // the edit happened and keeps rendering holiday/duration data from before the save).
  const allFinal = [...acts, ...mils];
  const maxDay = allFinal.reduce((m, a) => Math.max(m, a.end_day), 0);
  let newEndDate = data.end_date;
  if (data.start_date && maxDay > 0) {
    const start = parseDate(data.start_date);
    start.setDate(start.getDate() + maxDay - 1);
    newEndDate = formatLocalDate(start);
  }

  return { ...data, activities: acts, milestones: mils, total_duration_days: maxDay, end_date: newEndDate };
}

// ── Critical Path Method (CPM) ───────────────────────────────────────────────
// Uses existing start_day/end_day as ES/EF (already computed by cascade/recompute).
// Backward pass computes LS/LF. Float = LS - start_day. Float ≤ 0 → is_critical.
// Critical = "delaying this specific activity by one working day delays the project's
// overall end date". Rather than maintaining a second, separate backward-pass (LF/LS)
// formula per dependency type — which is exactly the kind of duplicated logic that already
// caused the FS/SS/FF/SF and weekend-lag bugs above, and stayed wrong in ~63% of chained
// cases even after trying to patch it directly — this reuses the SAME, already-verified
// cascadeFromActivity() that real drag/resize/save edits go through. Each activity is
// nudged on a throwaway copy of the data (never persisted) and the resulting cascade is
// checked against the real project end date. O(n) simulations x O(n) cascade — negligible
// for realistic schedule sizes.
export function computeCriticalPath(data: ScheduleData, projectStart?: Date, calendar?: ResolvedCalendar): ScheduleData {
  const allItems = [...data.activities, ...data.milestones];
  if (allItems.length === 0) return data;

  const baselineEnd = Math.max(...allItems.map(a => a.end_day));

  const isCriticalFor = (item: Activity): boolean => {
    // Nudge from the activity's EFFECTIVE (working-day) start, not its raw stored start_day.
    // A manually-typed/dragged date isn't weekend-guarded the way dependency cascades are, so
    // start_day can itself land on a Sat/Sun. Nudging from item.start_day + 1 in that case gets
    // silently swallowed by the very same weekend-skip that "corrects" the invalid start back
    // to the working day the activity was already effectively using — a 0-day nudge in
    // disguise, so nothing ever looked critical. Ground the nudge in the real working day first.
    const effectiveStart = projectStart ? nextWorkingDay(item.start_day, projectStart, calendar) : item.start_day;
    const nudgedStart = projectStart ? advanceWorkingDays(effectiveStart, 1, projectStart, calendar) : item.start_day + 1;
    if (nudgedStart <= item.start_day) return false; // guard, shouldn't happen
    const workDays = projectStart ? calToWork(item.start_day, item.end_day, projectStart, calendar) : Math.max(1, item.duration_days);
    const nudgedEnd = projectStart ? addWorkingDays(nudgedStart, workDays, projectStart, calendar) : nudgedStart + item.duration_days - 1;

    const patchNudge = (a: Activity) => a.id === item.id
      ? { ...a, start_day: nudgedStart, end_day: nudgedEnd, duration_days: nudgedEnd - nudgedStart + 1 }
      : a;
    const nudgedData: ScheduleData = {
      ...data,
      activities: data.activities.map(patchNudge),
      milestones: data.milestones.map(patchNudge),
    };
    const cascaded = cascadeFromActivity(item.id, nudgedData, projectStart, calendar);
    const newEnd = Math.max(...[...cascaded.activities, ...cascaded.milestones].map(a => a.end_day));
    return newEnd > baselineEnd;
  };

  const mark = (a: Activity): Activity => ({ ...a, is_critical: isCriticalFor(a) });

  return { ...data, activities: data.activities.map(mark), milestones: data.milestones.map(mark) };
}

// ── Working-day helpers ───────────────────────────────────────────────────────
export function subtractWorkingDays(fromDay: number, workingDays: number, projectStart: Date, calendar?: ResolvedCalendar): number {
  let cur = fromDay;
  let rem = workingDays;
  while (rem > 0) {
    if (isWorkingDayLocal(cur, projectStart, calendar)) rem--;
    if (rem > 0) cur--;
  }
  return cur;
}

export function addWorkingDays(startDay: number, workingDays: number, projectStart: Date, calendar?: ResolvedCalendar): number {
  let cur = startDay;
  let rem = workingDays;
  while (rem > 0) {
    if (isWorkingDayLocal(cur, projectStart, calendar)) rem--;
    if (rem > 0) cur++;
  }
  return cur;
}

export function calToWork(startDay: number, endDay: number, projectStart: Date, calendar?: ResolvedCalendar): number {
  let count = 0;
  for (let d = startDay; d <= endDay; d++) {
    if (isWorkingDayLocal(d, projectStart, calendar)) count++;
  }
  return Math.max(1, count);
}

// ── Robust JSON extraction ────────────────────────────────────────────────────
function extractJSON(text: string): any {
  // Try direct parse
  try { return JSON.parse(text); } catch {}
  // Strip markdown code fences
  const stripped = text.replace(/```(?:json)?\s*/gi, '').replace(/```\s*/g, '');
  try { return JSON.parse(stripped.trim()); } catch {}
  // Find outermost { }
  const start = stripped.indexOf('{');
  const end = stripped.lastIndexOf('}');
  if (start >= 0 && end > start) {
    try { return JSON.parse(stripped.slice(start, end + 1)); } catch {}
  }
  throw new Error('No valid JSON in AI response');
}

const WarnIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const Schedule: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit, onProjectUpdate}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.schedule;
  const C = M.common;
  const [activeTab, setActiveTab] = useState<'gantt' | 'wbs'>('gantt');
  const [scheduleData, setScheduleData] = useState<ScheduleData | null>(null);
  const [orgMembers, setOrgMembers] = useState<any[]>([]);
  const [scopeWPs, setScopeWPs] = useState<any[]>([]);
  const [scopeActivityExtras, setScopeActivityExtras] = useState<Record<string, { team_efforts?: Record<string, number> }>>({});
  const [budgetTeams, setBudgetTeams] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showWorkingCalendarModal, setShowWorkingCalendarModal] = useState(false);
  const [workingCalendarInitialTab, setWorkingCalendarInitialTab] = useState<'calendar' | 'week' | 'exceptions' | 'settings'>('calendar');
  const [calendarToast, setCalendarToast] = useState('');
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [saving, setSaving] = useState(false);
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set());
  const [showCriticalOnly, setShowCriticalOnly] = useState(false);
  const [dragging, setDragging] = useState<{ id: string; startX: number; originalStartDay: number } | null>(null);
  const [resizing, setResizing] = useState<{ id: string; edge: 'left' | 'right'; startX: number; originalStartDay: number; originalDurationDays: number } | null>(null);
  const [hoveredBarId, setHoveredBarId] = useState<string | null>(null);
  const [viewingActivity, setViewingActivity] = useState<Activity | null>(null);
  const wasDraggedRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [dragPhaseId, setDragPhaseId] = useState<string | null>(null);
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);
  const [editingPhaseTitle, setEditingPhaseTitle] = useState('');
  const [dragOverPhaseId, setDragOverPhaseId] = useState<string | null>(null);
  const ganttScrollRef = useRef<HTMLDivElement>(null);
  const ganttHeaderScrollRef = useRef<HTMLDivElement>(null); // horizontally mirrors ganttScrollRef so the frozen header stays aligned
  const [dayWidth, setDayWidth] = useState(DEFAULT_dayWidth);
  const [tsDate, setTsDate] = useState(new Date().toISOString().slice(0, 10));
  const [tsMd, setTsMd] = useState('');
  const [tsTeam, setTsTeam] = useState('');
  const [tsProgress, setTsProgress] = useState('');
  const [tsNote, setTsNote] = useState('');
  const [tsError, setTsError] = useState('');
  const [editingEntry, setEditingEntry] = useState<{ id: string; date: string; md: string; team: string; progress: string; note: string } | null>(null);

  const [form, setForm] = useState<Partial<Activity & { wbs_code: string; _startDate?: string; _endDate?: string }>>({
    title: '', description: '', type: 'activity', phase_id: '',
    duration_days: 1, start_day: 1, dependencies: [] as DependencyLink[],
    is_critical: false, is_milestone: false, assigned_to: [], status: 'not_started',
    progress_pct: 0, _startDate: '', _endDate: '',
  });

  const [phaseForm, setPhaseForm] = useState({ title: '', description: '', color: '#4f46e5', insertAfter: '__end__' });
  const phaseColors = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  // Sequential display codes per phase: activities → "1.1", "1.2", milestones → "M1"
  // wbs_code on the data object is left intact (used by Scope module matching)
  const displayCodes = React.useMemo(() => {
    const map = new Map<string, string>();
    if (!scheduleData) return map;
    scheduleData.phases.forEach((phase, pi) => {
      const phaseNum = pi + 1;
      const acts = scheduleData.activities.filter(a => a.phase_id === phase.id).sort((a, b) => a.start_day - b.start_day);
      acts.forEach((a, i) => map.set(a.id, `${phaseNum}.${i + 1}`));
      const mils = scheduleData.milestones.filter(m => m.phase_id === phase.id).sort((a, b) => a.start_day - b.start_day);
      mils.forEach((m, i) => map.set(m.id, `M${phaseNum}${mils.length > 1 ? `.${i + 1}` : ''}`));
    });
    return map;
  }, [scheduleData]);

  const dc = (item: { id: string; wbs_code: string }) => displayCodes.get(item.id) || item.wbs_code;

  const calendarRangeTo = React.useMemo(() => {
    if (!scheduleData?.start_date) return null;
    const allDays = [...scheduleData.activities, ...scheduleData.milestones].map(a => a.end_day || a.start_day || 1);
    const maxEndDay = allDays.length ? Math.max(...allDays, scheduleData.total_duration_days || 0) : (scheduleData.total_duration_days || 365);
    const d = parseDate(scheduleData.start_date);
    d.setDate(d.getDate() + maxEndDay + 30);
    return formatLocalDate(d);
  }, [scheduleData?.start_date, scheduleData?.activities, scheduleData?.milestones, scheduleData?.total_duration_days]);
  // Bumped on every Working Calendar modal save (even ones that don't touch calendar_id or
  // shift the date range) so useResolvedCalendar always refetches fresh holiday/exception
  // data instead of silently keeping whatever it had resolved before the edit.
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const resolvedCalendar = useResolvedCalendar(project.calendar_id, scheduleData?.start_date, calendarRangeTo, token, calendarRefreshKey);

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!WORKING_CALENDAR_ENABLED) return;
    // Self-heal a stale cached project object: selectedProject lives in App.tsx state
    // (persisted to localStorage) and is only otherwise refreshed when the user
    // re-selects the project from the list — a calendar switched from inside this very
    // module updates it going forward (see onProjectUpdate below), but anyone who already
    // had this project open before that fix, or across a page reload, needs this to pick
    // up the real calendar_id instead of silently running on the Standard-calendar fallback.
    axios.get(`${API}/projects/${project.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const fresh = res.data;
        if (fresh && fresh.calendar_id !== project.calendar_id) onProjectUpdate?.(fresh);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Working Calendar: the calendar resolves asynchronously (after schedule data loads,
  // since it needs the project's date range) — re-run critical-path marking once it does,
  // so is_critical reflects the real calendar instead of the Standard fallback it was
  // computed with on first load.
  useEffect(() => {
    if (resolvedCalendar && scheduleData?.start_date) {
      const ps = parseDate(scheduleData.start_date);
      setScheduleData(prev => prev ? computeCriticalPath(prev, ps, resolvedCalendar) : prev);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedCalendar]);

  useEffect(() => {
    if (showCriticalOnly && scheduleData) {
      const _ps = scheduleData.start_date ? parseDate(scheduleData.start_date) : undefined;
      setScheduleData(computeCriticalPath(scheduleData, _ps, resolvedCalendar));
    }
    // Pre-existing pattern: only meant to re-run when the toggle itself changes, reading
    // current scheduleData/resolvedCalendar at that moment (not on every data change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCriticalOnly]);

  useEffect(() => {
    setTsMd(''); setTsTeam(''); setTsProgress(''); setTsNote(''); setTsError('');
  }, [viewingActivity]);

  const fetchData = async () => {
    try {
      const [scheduleRes, stakeholdersRes, scopeRes, budgetRes] = await Promise.all([
        axios.get(`${API}/modules/${project.id}/Schedule`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/modules/${project.id}/Stakeholders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/modules/${project.id}/Scope`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/modules/${project.id}/Budget`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (scopeRes.data?.data?.work_packages) setScopeWPs(scopeRes.data.data.work_packages);
      if (scopeRes.data?.data?.activity_extras) setScopeActivityExtras(scopeRes.data.data.activity_extras);
      if (budgetRes.data?.data?.teams) setBudgetTeams((budgetRes.data.data.teams as any[]).map(t => t.name).filter(Boolean));
      if (scheduleRes.data?.data) {
        const data = scheduleRes.data.data;
        const loaded: ScheduleData = {
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          total_duration_days: data.total_duration_days || 365,
          phases: (data.phases || []).sort((a: Phase, b: Phase) => a.order - b.order),
          activities: (data.activities || []).filter((a: any) => !a.is_milestone).map((a: any) => ({ ...a, status: a.status || 'not_started', phase_id: a.phase_id || 'unassigned', progress_pct: a.progress_pct ?? 0, dependencies: normalizeDeps(a.dependencies) })),
          milestones: (data.milestones || []).map((m: any) => ({ ...m, status: m.status || 'not_started', phase_id: m.phase_id || 'unassigned', progress_pct: m.progress_pct ?? 0, dependencies: normalizeDeps(m.dependencies) })),
        };
        const _loadPs = loaded.start_date ? parseDate(loaded.start_date) : undefined;
        setScheduleData(computeCriticalPath(loaded, _loadPs, resolvedCalendar));
      }
      if (stakeholdersRes.data?.data?.stakeholders) setOrgMembers(stakeholdersRes.data.data.stakeholders);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveSchedule = useCallback(async (updated: ScheduleData) => {
    await axios.put(
      `${API}/modules/${project.id}/Schedule`,
      { data: updated, userId: user.id },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }, [project.id, token, user.id]);

  const debouncedSave = useCallback((updated: ScheduleData) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveSchedule(updated), 800);
  }, [saveSchedule]);

  const openEditActivity = (item: Activity) => {
    const sd = scheduleData?.start_date || '';
    const ps = sd ? parseDate(sd) : null;
    const workDays = ps ? calToWork(item.start_day, item.end_day, ps, resolvedCalendar) : item.duration_days;
    setEditingActivity(item);
    setForm({
      ...item,
      duration_days: workDays,
      _startDate: dayToDate(sd, item.start_day),
      _endDate: dayToDate(sd, item.end_day),
    });
    setShowActivityModal(true);
  };

  const allItems = scheduleData ? [
    ...scheduleData.activities,
    ...scheduleData.milestones,
  ].sort((a, b) => a.start_day - b.start_day) : [];

  // Gantt width = max of: activities, phase end_days, total_duration_days
  // Phases can extend far beyond the last activity (e.g. 36-month managed services)
  const phaseMaxDay = scheduleData
    ? (scheduleData.phases || []).reduce((m, p) => Math.max(m, p.end_day || 0), 0)
    : 0;
  const actualMaxDay = scheduleData
    ? Math.max(
        ...[...scheduleData.activities, ...scheduleData.milestones].map(a => a.end_day),
        scheduleData.total_duration_days || 0,
        phaseMaxDay,
        1,
      )
    : 365;
  const todayDay = scheduleData?.start_date
    ? (() => { const t = new Date(); t.setHours(0,0,0,0); return Math.round((t.getTime() - parseDate(scheduleData.start_date).getTime()) / 86400000); })()
    : -1;
  const totalDays = actualMaxDay;
  // extend gantt to include today if project is overdue
  const effectiveMaxDay = Math.max(actualMaxDay, todayDay > 0 ? todayDay : 0);
  const ganttWidth = (effectiveMaxDay + 30) * dayWidth;

  const projStart = scheduleData?.start_date ? parseDate(scheduleData.start_date) : null;

  const MONTH_LABELS = L.months;
  const monthMarkers: { day: number; label: string; widthDays: number }[] = [];
  const weekMarkers: { day: number; label: string }[] = [];

  if (scheduleData?.start_date) {
    const projectStart = parseDate(scheduleData.start_date);
    const totalDaysWithPad = effectiveMaxDay + 30;
    const toDayOffset = (d: Date) => Math.round((d.getTime() - projectStart.getTime()) / 86400000);

    // First month starts at day 0 (project start), subsequent months at their 1st
    let cur = new Date(projectStart.getFullYear(), projectStart.getMonth(), 1);
    while (true) {
      const dayOffset = Math.max(0, toDayOffset(cur));
      if (dayOffset > totalDaysWithPad) break;
      const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const nextOffset = toDayOffset(next);
      monthMarkers.push({
        day: dayOffset,
        label: `${MONTH_LABELS[cur.getMonth()]} ${cur.getFullYear()}`,
        widthDays: Math.min(nextOffset, totalDaysWithPad) - dayOffset,
      });
      // Week markers within this month (1st, 8th, 15th, 22nd, 29th)
      for (let w = 0; w < 5; w++) {
        const weekDate = new Date(cur.getFullYear(), cur.getMonth(), 1 + w * 7);
        if (weekDate >= next) break;
        const wOff = toDayOffset(weekDate);
        if (wOff >= 0 && wOff <= totalDaysWithPad)
          weekMarkers.push({ day: wOff, label: `Ε${w + 1}` });
      }
      cur = next;
    }
  }

  const SHOW_DAYS_THRESHOLD = 20;
  const showDays = dayWidth >= SHOW_DAYS_THRESHOLD;
  const DAY_LABELS = L.days;
  const dayMarkers: { day: number; label: string; initial: string; isWeekend: boolean }[] = [];
  if (showDays && scheduleData?.start_date) {
    const projectStart = parseDate(scheduleData.start_date);
    for (let d = 0; d <= effectiveMaxDay + 30; d++) {
      const date = addCalendarDays(projectStart, d);
      const dow = date.getDay();
      const isNonWorking = resolvedCalendar ? !calIsWorkingDay(resolvedCalendar, date) : (dow === 0 || dow === 6);
      dayMarkers.push({ day: d, label: `${date.getDate()}`, initial: DAY_LABELS[dow][0], isWeekend: isNonWorking });
    }
  }

  // Non-working-day blocks (weekends under Standard, or whatever the project calendar
  // defines — custom work-weeks, holidays, exceptions) for Gantt row background pattern.
  // One block per non-working day (rather than assuming Sat+Sun pairs) so it stays correct
  // for any work-week shape, not just Mon-Fri.
  const weekendBlocks: { day: number }[] = [];
  if (scheduleData?.start_date) {
    const _ps = parseDate(scheduleData.start_date);
    for (let d = 0; d <= effectiveMaxDay + 30; d++) {
      const _date = addCalendarDays(_ps, d);
      const isNonWorking = resolvedCalendar ? !calIsWorkingDay(resolvedCalendar, _date) : (_date.getDay() === 0 || _date.getDay() === 6);
      if (isNonWorking) weekendBlocks.push({ day: d });
    }
  }

  const ZOOM_STEPS = [4, 6, 8, 12, 18, 26, 36];
  const zoomIn  = () => setDayWidth(w => ZOOM_STEPS[Math.min(ZOOM_STEPS.indexOf(w) + 1, ZOOM_STEPS.length - 1)] ?? w);
  const zoomOut = () => setDayWidth(w => ZOOM_STEPS[Math.max(ZOOM_STEPS.indexOf(w) - 1, 0)] ?? w);

  const getPhaseItems = (phaseId: string) =>
    allItems.filter(item => item.phase_id === phaseId).sort((a, b) => a.start_day - b.start_day);

  const togglePhase = (phaseId: string) => {
    setCollapsedPhases(prev => {
      const next = new Set(prev);
      if (next.has(phaseId)) next.delete(phaseId);
      else next.add(phaseId);
      return next;
    });
  };

  const handleDragStart = (e: React.MouseEvent, item: Activity) => {
    e.stopPropagation(); // don't let this bubble up into the canvas pan handler
    wasDraggedRef.current = false;
    if (!canEdit) return;
    e.preventDefault();
    setDragging({ id: item.id, startX: e.clientX, originalStartDay: item.start_day });
  };

  const handleResizeStart = (e: React.MouseEvent, item: Activity, edge: 'left' | 'right') => {
    e.preventDefault(); e.stopPropagation();
    if (!canEdit) return;
    setResizing({ id: item.id, edge, startX: e.clientX, originalStartDay: item.start_day, originalDurationDays: item.duration_days });
  };

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizing || !scheduleData) return;
    const deltaDays = Math.round((e.clientX - resizing.startX) / dayWidth);
    const item = [...scheduleData.activities, ...scheduleData.milestones].find(a => a.id === resizing.id);
    if (!item) return;
    let newStart = resizing.originalStartDay;
    let newDur = resizing.originalDurationDays;
    if (resizing.edge === 'right') {
      newDur = Math.max(1, resizing.originalDurationDays + deltaDays);
    } else {
      const origEnd = resizing.originalStartDay + resizing.originalDurationDays - 1;
      newStart = Math.max(1, Math.min(resizing.originalStartDay + deltaDays, origEnd));
      newDur = Math.max(1, origEnd - newStart + 1);
    }
    const updated = { ...item, start_day: newStart, duration_days: newDur, end_day: newStart + newDur - 1 };
    setScheduleData({
      ...scheduleData,
      activities: scheduleData.activities.map(a => a.id === resizing.id ? updated : a),
      milestones: scheduleData.milestones.map(m => m.id === resizing.id ? updated : m),
    });
  }, [resizing, scheduleData, dayWidth]);

  const handleResizeUp = useCallback(() => {
    if (!resizing || !scheduleData) return;
    const _rps = scheduleData.start_date ? parseDate(scheduleData.start_date) : undefined;
    const cascaded = computeCriticalPath(cascadeFromActivity(resizing.id, scheduleData, _rps, resolvedCalendar), _rps, resolvedCalendar);
    setResizing(null);
    setScheduleData(cascaded);
    debouncedSave(cascaded);
  }, [resizing, scheduleData, debouncedSave, resolvedCalendar]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging || !scheduleData) return;
    const deltaX = e.clientX - dragging.startX;
    const deltaDays = Math.round(deltaX / dayWidth);
    if (Math.abs(deltaDays) > 0) wasDraggedRef.current = true;
    const newStartDay = Math.max(1, dragging.originalStartDay + deltaDays);
    const item = [...scheduleData.activities, ...scheduleData.milestones].find(a => a.id === dragging.id);
    if (!item) return;
    const newEndDay = newStartDay + item.duration_days - 1;
    const updatedItem = { ...item, start_day: newStartDay, end_day: newEndDay };
    const updatedSchedule: ScheduleData = {
      ...scheduleData,
      activities: scheduleData.activities.map(a => a.id === dragging.id ? updatedItem : a),
      milestones: scheduleData.milestones.map(m => m.id === dragging.id ? updatedItem : m),
    };
    setScheduleData(updatedSchedule);
  }, [dragging, scheduleData]);

  const handleMouseUp = useCallback(() => {
    if (!dragging || !scheduleData) return;
    const _dps = scheduleData.start_date ? parseDate(scheduleData.start_date) : undefined;
    const cascaded = computeCriticalPath(cascadeFromActivity(dragging.id, scheduleData, _dps, resolvedCalendar), _dps, resolvedCalendar);
    setDragging(null);
    setScheduleData(cascaded);
    debouncedSave(cascaded);
  }, [dragging, scheduleData, debouncedSave, resolvedCalendar]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (resizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeUp);
    };
  }, [resizing, handleResizeMove, handleResizeUp]);

  // ── Click-and-drag panning of the Gantt canvas (grab the empty background, drag left/right) ──
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef<{ startX: number; startScrollLeft: number; moved: boolean } | null>(null);

  const handlePanStart = (e: React.MouseEvent) => {
    if (e.button !== 0 || !ganttScrollRef.current) return; // left click only
    panRef.current = { startX: e.clientX, startScrollLeft: ganttScrollRef.current.scrollLeft, moved: false };
    setIsPanning(true);
  };

  const handlePanMove = useCallback((e: MouseEvent) => {
    if (!panRef.current || !ganttScrollRef.current) return;
    const dx = e.clientX - panRef.current.startX;
    if (Math.abs(dx) > 2) panRef.current.moved = true;
    ganttScrollRef.current.scrollLeft = panRef.current.startScrollLeft - dx;
  }, []);

  const handlePanEnd = useCallback(() => {
    panRef.current = null;
    setIsPanning(false);
  }, []);

  useEffect(() => {
    if (isPanning) {
      window.addEventListener('mousemove', handlePanMove);
      window.addEventListener('mouseup', handlePanEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handlePanMove);
      window.removeEventListener('mouseup', handlePanEnd);
    };
  }, [isPanning, handlePanMove, handlePanEnd]);

  const handleInlineEdit = async (id: string, isMilestone: boolean, field: string, value: any) => {
    if (!scheduleData || !canEdit) return;
    const ps = scheduleData.start_date ? parseDate(scheduleData.start_date) : null;
    const updateFn = (item: Activity) => {
      if (item.id !== id) return item;
      if (field === 'duration_days' && ps) {
        const calEnd = addWorkingDays(item.start_day, value, ps, resolvedCalendar);
        return { ...item, duration_days: calEnd - item.start_day + 1, end_day: calEnd };
      }
      if (field === 'end_day') {
        const newEnd = Math.max(item.start_day, value);
        return { ...item, end_day: newEnd, duration_days: newEnd - item.start_day + 1 };
      }
      const updated = { ...item, [field]: value };
      if (field === 'start_day') {
        updated.end_day = ps ? addWorkingDays(updated.start_day, calToWork(item.start_day, item.end_day, ps, resolvedCalendar), ps, resolvedCalendar) : updated.start_day + updated.duration_days - 1;
      }
      return updated;
    };
    let updatedSchedule: ScheduleData = {
      ...scheduleData,
      activities: scheduleData.activities.map(updateFn),
      milestones: scheduleData.milestones.map(updateFn),
    };
    if (field === 'start_day' || field === 'duration_days' || field === 'end_day') {
      const _ips = updatedSchedule.start_date ? parseDate(updatedSchedule.start_date) : undefined;
      updatedSchedule = computeCriticalPath(cascadeFromActivity(id, updatedSchedule, _ips, resolvedCalendar), _ips, resolvedCalendar);
    }
    setScheduleData(updatedSchedule);
    debouncedSave(updatedSchedule);
  };

  const handleSaveActivity = async () => {
    if (!form.title || !scheduleData) return;
    setSaving(true);
    try {
      const newActivity: Activity = {
        id: editingActivity?.id || `A${Date.now()}`,
        phase_id: form.phase_id || scheduleData.phases[0]?.id || 'unassigned',
        wbs_code: (() => { const wp = editingActivity ? scopeWPs.find(w => w.activity_ids?.includes(editingActivity.id)) : null; return wp ? wp.code : (form.wbs_code || ''); })(),
        title: form.title || '',
        description: form.description || '',
        type: form.type || 'activity',
        duration_days: (() => {
          if (form.type === 'milestone') return 0;
          const ps2 = scheduleData.start_date ? parseDate(scheduleData.start_date) : null;
          const calEnd = ps2 ? addWorkingDays(form.start_day || 1, form.duration_days || 1, ps2, resolvedCalendar) : (form.start_day || 1) + (form.duration_days || 1) - 1;
          return calEnd - (form.start_day || 1) + 1;
        })(),
        start_day: form.start_day || 1,
        end_day: (() => {
          if (form.type === 'milestone') return form.start_day || 1;
          const ps2 = scheduleData.start_date ? parseDate(scheduleData.start_date) : null;
          return ps2 ? addWorkingDays(form.start_day || 1, form.duration_days || 1, ps2, resolvedCalendar) : (form.start_day || 1) + (form.duration_days || 1) - 1;
        })(),
        dependencies: form.dependencies || [],
        is_critical: form.is_critical || false,
        is_milestone: form.type === 'milestone',
        assigned_to: form.assigned_to || [],
        status: form.status || 'not_started',
        progress_pct: form.status === 'completed' ? 100 : (form.progress_pct ?? 0),
      };
      // An edit can change `type` between 'milestone' and 'activity' for an EXISTING item —
      // that item must move from its old array (milestones/activities) to the other one, not
      // get map-replaced in place: map() only replaces an entry it can find by id, and a
      // milestone being edited into an activity has its id in `milestones`, not `activities`
      // (or vice versa), so a plain map-replace on the "new" array silently matches nothing
      // and the edit is lost — the old entry stays untouched and Save appears to do nothing.
      let updatedSchedule: ScheduleData;
      const typeChanged = !!editingActivity && editingActivity.is_milestone !== newActivity.is_milestone;
      if (newActivity.is_milestone) {
        const updatedMilestones = editingActivity && !typeChanged
          ? scheduleData.milestones.map(m => m.id === editingActivity.id ? newActivity : m)
          : [...scheduleData.milestones, newActivity];
        const updatedActivities = typeChanged
          ? scheduleData.activities.filter(a => a.id !== editingActivity!.id)
          : scheduleData.activities;
        updatedSchedule = { ...scheduleData, activities: updatedActivities, milestones: updatedMilestones };
      } else {
        const updatedActivities = editingActivity && !typeChanged
          ? scheduleData.activities.map(a => a.id === editingActivity.id ? newActivity : a)
          : [...scheduleData.activities, newActivity];
        const updatedMilestones = typeChanged
          ? scheduleData.milestones.filter(m => m.id !== editingActivity!.id)
          : scheduleData.milestones;
        updatedSchedule = { ...scheduleData, activities: updatedActivities, milestones: updatedMilestones };
      }
      // Enforce dependency constraints on the activity itself first — same weekend-skip +
      // working-day-preserving logic as the drag/resize cascade (computeConstrainedPosition),
      // so editing dependencies from this modal can no longer land a start on a weekend or
      // silently shrink the activity's working-day duration.
      const _sps = updatedSchedule.start_date ? parseDate(updatedSchedule.start_date) : undefined;
      if (newActivity.dependencies.length > 0) {
        const allCurrent = [...updatedSchedule.activities, ...updatedSchedule.milestones];
        const findItem = (id: string) => allCurrent.find(a => a.id === id);
        const links = normalizeDeps(newActivity.dependencies);
        const pos = computeConstrainedPosition(newActivity, links, findItem, _sps, resolvedCalendar);
        if (pos) {
          const patch = { ...newActivity, start_day: pos.start_day, end_day: pos.end_day, duration_days: pos.end_day - pos.start_day + 1 };
          updatedSchedule = newActivity.is_milestone
            ? { ...updatedSchedule, milestones: updatedSchedule.milestones.map(m => m.id === newActivity.id ? patch : m) }
            : { ...updatedSchedule, activities: updatedSchedule.activities.map(a => a.id === newActivity.id ? patch : a) };
        }
      }
      // Cascade successors + recompute critical path
      updatedSchedule = computeCriticalPath(cascadeFromActivity(newActivity.id, updatedSchedule, _sps, resolvedCalendar), _sps, resolvedCalendar);
      await saveSchedule(updatedSchedule);
      setScheduleData(updatedSchedule);
      resetForm();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleSavePhase = async () => {
    if (!phaseForm.title || !scheduleData) return;
    setSaving(true);
    try {
      const sorted = [...scheduleData.phases].sort((a, b) => a.order - b.order);

      // Determine insert index in sorted array
      let insertIndex: number;
      if (phaseForm.insertAfter === '__start__') {
        insertIndex = 0;
      } else if (phaseForm.insertAfter === '__end__' || !phaseForm.insertAfter) {
        insertIndex = sorted.length;
      } else {
        const refIdx = sorted.findIndex(p => p.id === phaseForm.insertAfter);
        insertIndex = refIdx === -1 ? sorted.length : refIdx + 1;
      }

      const newPhase: Phase = {
        id: `PH${Date.now()}`,
        title: phaseForm.title,
        description: phaseForm.description,
        color: phaseForm.color,
        order: insertIndex + 1,
      };

      // Shift order of phases that come after the insertion point
      const reordered = sorted.map((p, i) => i >= insertIndex ? { ...p, order: p.order + 1 } : p);
      const finalPhases = [...reordered.slice(0, insertIndex), newPhase, ...reordered.slice(insertIndex)];

      const updatedSchedule = { ...scheduleData, phases: finalPhases };
      await saveSchedule(updatedSchedule);
      setScheduleData(updatedSchedule);
      setPhaseForm({ title: '', description: '', color: '#4f46e5', insertAfter: '__end__' });
      setShowPhaseModal(false);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleRenamePhase = async (phaseId: string) => {
    if (!scheduleData || !editingPhaseTitle.trim()) { setEditingPhaseId(null); return; }
    const updated = { ...scheduleData, phases: scheduleData.phases.map(p => p.id === phaseId ? { ...p, title: editingPhaseTitle.trim() } : p) };
    await saveSchedule(updated);
    setScheduleData(updated);
    setEditingPhaseId(null);
  };

  const handleDeletePhase = async (phaseId: string) => {
    if (!scheduleData) return;
    const updatedActivities = scheduleData.activities.map(a => a.phase_id === phaseId ? { ...a, phase_id: 'unassigned' } : a);
    const updatedMilestones = scheduleData.milestones.map(m => m.phase_id === phaseId ? { ...m, phase_id: 'unassigned' } : m);
    const updatedSchedule = { ...scheduleData, phases: scheduleData.phases.filter(p => p.id !== phaseId), activities: updatedActivities, milestones: updatedMilestones };
    await saveSchedule(updatedSchedule);
    setScheduleData(updatedSchedule);
  };

  const handleAddTimeEntry = (activityId: string, currentProgress: number) => {
    const md = parseFloat(tsMd);
    const progress = parseFloat(tsProgress);
    setTsError('');
    if (!scheduleData || !md || md <= 0) return;
    if (!tsTeam) { setTsError(L.validationTeam); return; }
    if (isNaN(progress) || tsProgress === '') { setTsError(L.validationProgress); return; }
    if (progress < currentProgress) { setTsError(L.timesheetProgressError(currentProgress)); return; }
    if (progress < 0 || progress > 100) { setTsError(L.validationProgressRange); return; }
    const entry: TimeEntry = { id: `TE${Date.now()}`, date: tsDate, md, team: tsTeam, progress_pct: progress, note: tsNote || undefined };
    const patch = (a: Activity): Activity => {
      if (a.id !== activityId) return a;
      const entries = [...(a.time_entries || []), entry];
      const pct = latestProgress(entries);
      const status: Activity['status'] = pct === 100 ? 'completed' : pct > 0 ? 'in_progress' : a.status;
      return { ...a, time_entries: entries, progress_pct: pct, status };
    };
    const updated = { ...scheduleData, activities: scheduleData.activities.map(patch), milestones: scheduleData.milestones.map(patch) };
    setScheduleData(updated);
    setViewingActivity(updated.activities.find(a => a.id === activityId) || updated.milestones.find(a => a.id === activityId) || null);
    setTsMd(''); setTsTeam(''); setTsProgress(''); setTsNote('');
    saveSchedule(updated);
  };

  const latestProgress = (entries: TimeEntry[]): number => {
    if (!entries.length) return 0;
    const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
    return sorted[sorted.length - 1].progress_pct ?? 0;
  };

  const handleDeleteTimeEntry = (activityId: string, entryId: string) => {
    if (!scheduleData) return;
    const patch = (a: Activity): Activity => {
      if (a.id !== activityId) return a;
      const entries = (a.time_entries || []).filter(e => e.id !== entryId);
      const pct = latestProgress(entries);
      const status: Activity['status'] = pct === 100 ? 'completed' : pct > 0 ? 'in_progress' : 'not_started';
      return { ...a, time_entries: entries, progress_pct: pct, status };
    };
    const updated = { ...scheduleData, activities: scheduleData.activities.map(patch), milestones: scheduleData.milestones.map(patch) };
    setScheduleData(updated);
    setViewingActivity(updated.activities.find(a => a.id === activityId) || updated.milestones.find(a => a.id === activityId) || null);
    saveSchedule(updated);
  };

  const handleSaveEditEntry = (activityId: string) => {
    if (!scheduleData || !editingEntry) return;
    const md = parseFloat(editingEntry.md);
    const progress = parseFloat(editingEntry.progress);
    if (!md || md <= 0 || !editingEntry.team || isNaN(progress)) return;
    const patch = (a: Activity): Activity => {
      if (a.id !== activityId) return a;
      const entries = (a.time_entries || []).map(e => e.id === editingEntry.id
        ? { ...e, date: editingEntry.date, md, team: editingEntry.team, progress_pct: progress, note: editingEntry.note || undefined }
        : e);
      const pct = latestProgress(entries);
      const status: Activity['status'] = pct === 100 ? 'completed' : pct > 0 ? 'in_progress' : 'not_started';
      return { ...a, time_entries: entries, progress_pct: pct, status };
    };
    const updated = { ...scheduleData, activities: scheduleData.activities.map(patch), milestones: scheduleData.milestones.map(patch) };
    setScheduleData(updated);
    setViewingActivity(updated.activities.find(a => a.id === activityId) || updated.milestones.find(a => a.id === activityId) || null);
    setEditingEntry(null);
    saveSchedule(updated);
  };

  const handlePhaseDrop = (targetPhaseId: string) => {
    if (!scheduleData || !dragPhaseId || dragPhaseId === targetPhaseId) return;
    const sorted = [...scheduleData.phases].sort((a, b) => a.order - b.order);
    const fromIdx = sorted.findIndex(p => p.id === dragPhaseId);
    const toIdx = sorted.findIndex(p => p.id === targetPhaseId);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const withNewOrders = reordered.map((p, i) => ({ ...p, order: i + 1 }));
    const updatedSchedule = { ...scheduleData, phases: withNewOrders };
    // Optimistic update — save in background
    setScheduleData(updatedSchedule);
    setDragPhaseId(null);
    setDragOverPhaseId(null);
    saveSchedule(updatedSchedule);
  };

  const handleDeleteActivity = async (id: string, isMilestone: boolean) => {
    if (!scheduleData) return;
    const updatedSchedule = isMilestone
      ? { ...scheduleData, milestones: scheduleData.milestones.filter(m => m.id !== id) }
      : { ...scheduleData, activities: scheduleData.activities.filter(a => a.id !== id) };
    await saveSchedule(updatedSchedule);
    setScheduleData(updatedSchedule);
  };

  const handleStatusChange = async (id: string, isMilestone: boolean, status: Activity['status']) => {
    if (!scheduleData) return;
    const autoProgress = (item: Activity) => {
      if (status === 'completed') return 100;
      if (status === 'not_started') return 0;
      return item.progress_pct; // keep existing for in_progress / delayed
    };
    const patchItem = (item: Activity) => item.id === id ? { ...item, status, progress_pct: autoProgress(item) } : item;
    const updatedSchedule = isMilestone
      ? { ...scheduleData, milestones: scheduleData.milestones.map(patchItem) }
      : { ...scheduleData, activities: scheduleData.activities.map(patchItem) };
    await saveSchedule(updatedSchedule);
    setScheduleData(updatedSchedule);
  };

  const resetForm = () => {
    setForm({ title: '', description: '', type: 'activity', phase_id: '', duration_days: 1, start_day: 1, dependencies: [] as DependencyLink[], is_critical: false, is_milestone: false, assigned_to: [], status: 'not_started', progress_pct: 0, _startDate: '', _endDate: '' });
    setEditingActivity(null);
    setShowActivityModal(false);
  };

  // Build a map of activityId → Y-center in the Gantt content area.
  // Must account for borders (no global box-sizing:border-box).
  // Phase header: height:36 + borderTop:1 + borderBottom:1 = 38px rendered
  // Activity row: height:40 + borderBottom:1 = 41px rendered
  const buildRowPositions = (): Record<string, number> => {
    const pos: Record<string, number> = {};
    let y = 0;
    phases.forEach(phase => {
      y += PHASE_HEADER_H + 2; // 38px rendered (borderTop + borderBottom)
      if (!collapsedPhases.has(phase.id)) {
        getPhaseItems(phase.id).forEach(item => {
          pos[item.id] = y + ACTIVITY_ROW_H / 2;
          y += ACTIVITY_ROW_H + 1; // 41px rendered (borderBottom)
        });
      }
    });
    if (unassignedItems.length > 0) {
      y += PHASE_HEADER_H + 1; // 37px rendered (borderTop only)
      unassignedItems.forEach(item => {
        pos[item.id] = y + ACTIVITY_ROW_H / 2;
        y += ACTIVITY_ROW_H + 1;
      });
    }
    return pos;
  };

  const getStatusColor = (item: Activity) => {
    if (showCriticalOnly && item.is_critical) return '#ef4444';
    if (item.status === 'completed') return '#10b981';
    if (item.status === 'delayed') return '#ef4444';
    if (item.status === 'in_progress') return '#0ea5e9';
    if ((item as any).type === 'client_dependency') return '#f59e0b';
    return '#4f46e5';
  };

  const getStatusLabel = (status: string) => {
    return L.statusLabels[status] || L.statusLabels['not_started'];
  };

  // ── Excel export: a styled, readable Gantt chart (title/legend/weekly bars) ──
  const EXCEL_COLORS = {
    navy: 'FF1F3864', darkBlue: 'FF2E5395', lightBlue: 'FFD9E2F3',
    white: 'FFFFFFFF', grey: 'FFF2F2F2', border: 'FFD9D9D9', textGrey: 'FF595959',
    blue: 'FF4472C4', green: 'FF70AD47', orange: 'FFED7D31', red: 'FFEF4444', milestoneRed: 'FFC00000',
  };

  const fmtDMY = (iso: string): string => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const exportToExcel = async () => {
    if (!scheduleData || exporting) return;
    setExporting(true);
    try {
      const sd = scheduleData.start_date;
      const solid = (argb: string): ExcelJS.Fill => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
      const thinBorder: Partial<ExcelJS.Borders> = {
        top: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        left: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        bottom: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        right: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
      };

      const numWeeks = Math.max(1, Math.ceil(totalDays / 7));
      const weekStartDate = (w: number) => dayToDate(sd, w * 7 + 1);
      const weekEndDate = (w: number) => dayToDate(sd, w * 7 + 7);
      const FIXED_COLS = 8;
      const totalCols = FIXED_COLS + numWeeks;

      const wb = new ExcelJS.Workbook();
      wb.creator = 'eazyproject';
      wb.created = new Date();
      const ws = wb.addWorksheet('Gantt Chart', {
        views: [{ state: 'frozen', xSplit: FIXED_COLS, ySplit: 5, showGridLines: false }],
      });
      ws.columns = [
        { width: 4.5 }, { width: 50 }, { width: 18 }, { width: 15 }, { width: 11 }, { width: 11 }, { width: 11 }, { width: 34 },
        ...Array.from({ length: numWeeks }, () => ({ width: 4.4 })),
      ];

      let r = 1;

      // Row 1: title
      ws.mergeCells(r, 1, r, totalCols);
      const titleCell = ws.getCell(r, 1);
      titleCell.value = `${L.excelTitlePrefix} — ${project.name}`;
      titleCell.font = { name: 'Arial', bold: true, size: 15, color: { argb: EXCEL_COLORS.navy } };
      titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
      ws.getRow(r).height = 24;
      r++;

      // Row 2: subtitle (built from the project's real data)
      const activitiesN = scheduleData.activities.length;
      const milestonesN = scheduleData.milestones.length;
      ws.mergeCells(r, 1, r, totalCols);
      const subCell = ws.getCell(r, 1);
      subCell.value = L.excelSubtitle(fmtDMY(sd), totalDays, numWeeks, phases.length, activitiesN, milestonesN);
      subCell.font = { name: 'Arial', italic: true, size: 9, color: { argb: EXCEL_COLORS.textGrey } };
      subCell.alignment = { horizontal: 'left', vertical: 'middle' };
      ws.getRow(r).height = 16;
      r++;

      // Rows 3–4: month bands over the weekly columns, merged vertically (2 rows tall)
      // so "Μήνας N (dd/mm/yyyy–dd/mm/yyyy)" has room to wrap cleanly on its own two lines.
      const monthRowTop = r;
      const monthRowBottom = r + 1;
      ws.getRow(monthRowTop).height = 18;
      ws.getRow(monthRowBottom).height = 28;
      for (let c = 1; c <= FIXED_COLS; c++) {
        ws.getCell(monthRowTop, c).fill = solid(EXCEL_COLORS.white);
        ws.getCell(monthRowBottom, c).fill = solid(EXCEL_COLORS.white);
      }
      const numBands = Math.ceil(numWeeks / 4);
      for (let bIdx = 0; bIdx < numBands; bIdx++) {
        const firstWeek = bIdx * 4;
        const lastWeek = Math.min(firstWeek + 3, numWeeks - 1);
        const startCol = FIXED_COLS + 1 + firstWeek;
        const endCol = FIXED_COLS + 1 + lastWeek;
        for (let row = monthRowTop; row <= monthRowBottom; row++) {
          for (let c = startCol; c <= endCol; c++) {
            const cell = ws.getCell(row, c);
            cell.fill = solid(EXCEL_COLORS.lightBlue);
            cell.border = thinBorder;
          }
        }
        const label = ws.getCell(monthRowTop, startCol);
        label.value = `${L.excelMonthLabel(bIdx + 1)}\n(${fmtDMY(weekStartDate(firstWeek))}–${fmtDMY(weekEndDate(lastWeek))})`;
        label.font = { name: 'Arial', bold: true, size: 9, color: { argb: EXCEL_COLORS.navy } };
        label.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        ws.mergeCells(monthRowTop, startCol, monthRowBottom, endCol);
      }
      r = monthRowBottom + 1;

      // Row 5: column headers
      const headerRow = r;
      ws.getRow(headerRow).height = 34;
      const headers = [L.excelColNum, L.excelColTask, L.excelColOwner, L.excelColType, L.excelColStart, L.excelColDuration, L.excelColEnd, L.excelColDep];
      headers.forEach((h, i) => {
        const cell = ws.getCell(headerRow, i + 1);
        cell.value = h;
        cell.font = { name: 'Arial', bold: true, size: 9, color: { argb: EXCEL_COLORS.white } };
        cell.fill = solid(EXCEL_COLORS.darkBlue);
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = thinBorder;
      });
      for (let w = 0; w < numWeeks; w++) {
        const cell = ws.getCell(headerRow, FIXED_COLS + 1 + w);
        cell.value = fmtDMY(weekStartDate(w)).slice(0, 5);
        cell.font = { name: 'Arial', bold: true, size: 7.5, color: { argb: EXCEL_COLORS.white } };
        cell.fill = solid(EXCEL_COLORS.darkBlue);
        cell.alignment = { horizontal: 'center', vertical: 'middle', textRotation: 90 };
        cell.border = thinBorder;
      }
      r++;

      // ── Section band + data rows ──
      let seq = 0; // global sequential # and zebra counter, matches the reference template
      // Phase band: title on the left, and the phase's total workload — the sum of each
      // activity's own duration_days (not the calendar span) — as a number in the same
      // column position as the per-row "Διάρκεια" figure.
      const renderBand = (title: string, items: Activity[]) => {
        const sumDays = items.reduce((s, i) => s + (i.is_milestone ? 0 : (i.duration_days || 0)), 0);

        ws.mergeCells(r, 1, r, 5);
        const titleCell = ws.getCell(r, 1);
        titleCell.value = title;
        titleCell.font = { name: 'Arial', bold: true, size: 10.5, color: { argb: EXCEL_COLORS.white } };
        titleCell.fill = solid(EXCEL_COLORS.navy);
        titleCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };

        const durCell = ws.getCell(r, 6);
        durCell.value = sumDays > 0 ? `${sumDays} ${L.daysAbbr}` : '—';
        durCell.font = { name: 'Arial', bold: true, size: 9.5, color: { argb: EXCEL_COLORS.white } };
        durCell.fill = solid(EXCEL_COLORS.navy);
        durCell.alignment = { horizontal: 'center', vertical: 'middle' };

        for (let c = 7; c <= totalCols; c++) ws.getCell(r, c).fill = solid(EXCEL_COLORS.navy);
        ws.getRow(r).height = 19;
        r++;
      };

      const depTextFor = (item: Activity): string => {
        const preds = normalizeDeps(item.dependencies)
          .map(d => allItems.find(a => a.id === d.id)?.title)
          .filter(Boolean);
        const parts: string[] = [];
        if (preds.length) parts.push(`${L.excelDepAfter} ${preds.join(', ')}`);
        if (item.description) parts.push(item.description);
        return parts.join(' — ');
      };

      const barColorFor = (item: Activity): string => {
        if (item.status === 'completed') return EXCEL_COLORS.green;
        if (item.is_critical || item.status === 'delayed') return EXCEL_COLORS.red;
        if (item.type === 'client_dependency') return EXCEL_COLORS.orange;
        return EXCEL_COLORS.blue;
      };

      const renderItem = (item: Activity) => {
        seq++;
        const bg = seq % 2 === 0 ? EXCEL_COLORS.grey : EXCEL_COLORS.white;
        const typeLabel = item.is_milestone ? L.excelTypeMilestone : item.type === 'client_dependency' ? L.excelTypeClientDep : L.excelTypeActivity;
        const values = [
          seq, item.title, item.assigned_to?.length ? item.assigned_to.join(', ') : L.excelNoOwner, typeLabel,
          fmtDMY(dayToDate(sd, item.start_day)), item.is_milestone ? '—' : item.duration_days,
          fmtDMY(dayToDate(sd, item.end_day)), depTextFor(item),
        ];
        values.forEach((v, i) => {
          const cell = ws.getCell(r, i + 1);
          cell.value = v as any;
          cell.font = { name: 'Arial', bold: item.is_milestone, size: 9 };
          cell.alignment = { horizontal: i === 0 || i === 3 ? 'center' : 'left', vertical: 'middle', wrapText: i === 1 || i === 7 };
          cell.fill = solid(bg);
          cell.border = thinBorder;
        });

        const startWeek = Math.floor((item.start_day - 1) / 7);
        const endWeek = Math.floor((item.end_day - 1) / 7);
        const barColor = barColorFor(item);
        for (let w = 0; w < numWeeks; w++) {
          const cell = ws.getCell(r, FIXED_COLS + 1 + w);
          cell.border = thinBorder;
          if (item.is_milestone) {
            if (w === startWeek) {
              cell.value = '◆';
              cell.font = { name: 'Arial', bold: true, size: 10, color: { argb: EXCEL_COLORS.milestoneRed } };
              cell.alignment = { horizontal: 'center', vertical: 'middle' };
              cell.fill = solid(EXCEL_COLORS.white);
            } else {
              cell.fill = solid(bg);
            }
          } else if (w >= startWeek && w <= endWeek) {
            cell.fill = solid(barColor);
          } else {
            cell.fill = solid(bg);
          }
        }
        ws.getRow(r).height = 32;
        r++;
      };

      if (phases.length > 0) {
        phases.forEach(phase => {
          const items = getPhaseItems(phase.id);
          renderBand(phase.title, items);
          items.forEach(renderItem);
        });
        if (unassignedItems.length > 0) {
          renderBand(L.defaultPhaseTitle, unassignedItems);
          unassignedItems.forEach(renderItem);
        }
      } else if (allItems.length > 0) {
        renderBand(L.defaultPhaseTitle, allItems);
        allItems.forEach(renderItem);
      }

      // ── Legend ──
      r++;
      const legendItems: { color: string; label: string; diamond?: boolean }[] = [
        { color: EXCEL_COLORS.blue, label: L.excelLegendActivity },
        { color: EXCEL_COLORS.green, label: L.excelLegendCompleted },
        { color: EXCEL_COLORS.orange, label: L.excelLegendClientDep },
        { color: EXCEL_COLORS.red, label: L.excelLegendCritical },
        { color: EXCEL_COLORS.milestoneRed, label: L.excelLegendMilestone, diamond: true },
      ];
      const legendTitleCell = ws.getCell(r, 1);
      legendTitleCell.value = L.excelLegendTitle;
      legendTitleCell.font = { name: 'Arial', bold: true, size: 10 };
      r++;
      legendItems.forEach(li => {
        const swatch = ws.getCell(r, 1);
        if (li.diamond) {
          swatch.value = '◆';
          swatch.font = { name: 'Arial', bold: true, size: 11, color: { argb: li.color } };
          swatch.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          swatch.fill = solid(li.color);
          swatch.border = thinBorder;
        }
        ws.mergeCells(r, 2, r, 8);
        const label = ws.getCell(r, 2);
        label.value = li.label;
        label.font = { name: 'Arial', size: 8.5 };
        label.alignment = { horizontal: 'left', vertical: 'middle' };
        r++;
      });

      // ── Footnote ──
      r++;
      ws.mergeCells(r, 1, r, totalCols);
      const footCell = ws.getCell(r, 1);
      footCell.value = L.excelFootnote;
      footCell.font = { name: 'Arial', italic: true, size: 8.5, color: { argb: EXCEL_COLORS.textGrey } };
      footCell.alignment = { vertical: 'top', wrapText: true };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const safeName = project.name.replace(/[\\/:*?"<>|]/g, '_');
      saveAs(blob, `Gantt_${safeName}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const unassignedItems = allItems.filter(item => !item.phase_id || item.phase_id === 'unassigned');
  const phases = scheduleData?.phases || [];


  return (
    <div style={styles.container}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink} onClick={onBack}>{C.myProjects}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbLink} onClick={onBack}>{project.name}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbCurrent}>Schedule</span>
      </div>

      <div style={styles.main}>
        <div style={styles.titleRow}>
          <h2 style={styles.pageTitle}>Schedule</h2>
          <div style={styles.titleButtons}>
            {WORKING_CALENDAR_ENABLED && (
              <button style={styles.calendarBtn} onClick={() => { setWorkingCalendarInitialTab('calendar'); setShowWorkingCalendarModal(true); }}>
                📅 {M.workingCalendar.isProjectCalendar}
              </button>
            )}
            <button style={{ ...styles.exportBtn, opacity: exporting ? 0.6 : 1, cursor: exporting ? 'not-allowed' : 'pointer' }} onClick={exportToExcel} disabled={exporting}>
              {exporting ? L.excelExporting : L.excelBtnLabel}
            </button>
            {canEdit && (
              <>
                <button style={styles.phaseBtn} onClick={() => { setPhaseForm({ title: '', description: '', color: '#4f46e5', insertAfter: '__end__' }); setShowPhaseModal(true); }}>{L.btnNewPhase}</button>
                <button style={styles.addBtn} onClick={() => { const sd = scheduleData?.start_date || ''; setForm(f => ({ ...f, _startDate: sd, _endDate: sd })); setShowActivityModal(true); }}>{L.btnNewActivity}</button>
              </>
            )}
          </div>
        </div>

        <div style={styles.tabWrapper}>
          <div style={styles.tabContainer}>
            <button style={{ ...styles.tab, ...(activeTab === 'gantt' ? styles.tabActive : styles.tabInactive) }} onClick={() => setActiveTab('gantt')}>Project Schedule (Gantt)</button>
            <button style={{ ...styles.tab, ...(activeTab === 'wbs' ? styles.tabActive : styles.tabInactive) }} onClick={() => setActiveTab('wbs')}>Activity List</button>
          </div>
        </div>

        {loading ? (
          <p style={styles.loading}>{C.loading}</p>
        ) : !scheduleData || (allItems.length === 0 && phases.length === 0) ? (
          <div style={styles.emptyState}><p>{L.emptyState}</p></div>
        ) : (
          <>
            {activeTab === 'gantt' && (
              <div style={styles.section}>
                {/* Summary */}
                <div style={styles.summaryRow}>
                  <div style={styles.summaryCard}><div style={styles.summaryValue}>{phases.length}</div><div style={styles.summaryLabel}>{L.summaryPhases}</div></div>
                  <div style={styles.summaryCard}><div style={styles.summaryValue}>{scheduleData.activities.length}</div><div style={styles.summaryLabel}>Activities</div></div>
                  {(() => {
                    const projectStart = scheduleData.start_date ? parseDate(scheduleData.start_date) : null;
                    // Use stored end_day directly — avoids duration_days || 1 bug for milestones (duration_days=0)
                    const activities = scheduleData.activities || [];
                    const allItems = [...activities, ...(scheduleData.milestones || [])];
                    const minStartDay = allItems.length > 0 ? Math.min(...allItems.map((a: any) => a.start_day || 1)) : 1;
                    const actMaxEndDay = allItems.length > 0 ? Math.max(...allItems.map((a: any) => a.end_day || a.start_day || 1)) : 1;
                    // Use only actual activities/milestones — total_duration_days is stale after manual edits
                    const phaseMaxEndDay = (scheduleData.phases || []).reduce((m, p) => Math.max(m, p.end_day || 0), 0);
                    const maxEndDay = allItems.length > 0 ? Math.max(actMaxEndDay, phaseMaxEndDay) : (scheduleData.total_duration_days || 0);
                    const computedStart = projectStart ? addCalendarDays(projectStart, minStartDay - 1) : null;
                    const computedEnd = projectStart ? addCalendarDays(projectStart, maxEndDay - 1) : null;
                    const durationDays = projectStart ? calToWork(minStartDay, maxEndDay, projectStart, resolvedCalendar) : maxEndDay - minStartDay + 1;
                    const calendarDays = maxEndDay - minStartDay + 1;
                    return (<>
                      {WORKING_CALENDAR_ENABLED ? (
                        <div
                          style={{ ...styles.summaryCard, ...styles.calendarKpiClickable }}
                          onClick={() => { setWorkingCalendarInitialTab('week'); setShowWorkingCalendarModal(true); }}
                          title={M.workingCalendar.dropdownEdit}
                        >
                          <div style={styles.summaryValue}>{durationDays}</div>
                          <div style={styles.summaryLabel}>{L.summaryDays} · {calendarDays} {M.workingCalendar.calendarDaysSuffix}</div>
                        </div>
                      ) : (
                        <div style={styles.summaryCard}><div style={styles.summaryValue}>{durationDays}</div><div style={styles.summaryLabel}>{L.summaryDays}</div></div>
                      )}
                      <div style={styles.summaryCard}><div style={styles.summaryValue}>{computedStart ? computedStart.toLocaleDateString(C.dateLocale) : '—'}</div><div style={styles.summaryLabel}>{L.summaryStart}</div></div>
                      <div style={styles.summaryCard}><div style={styles.summaryValue}>{computedEnd ? computedEnd.toLocaleDateString(C.dateLocale) : '—'}</div><div style={styles.summaryLabel}>{L.summaryEnd}</div></div>
                    </>);
                  })()}
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <div style={styles.ganttLegend}>
                    <div style={styles.legendItem}><div style={{ ...styles.legendBox, backgroundColor: '#4f46e5' }} /><span>Activity</span></div>
                    <div style={styles.legendItem}><div style={{ ...styles.legendBox, backgroundColor: '#ef4444' }} /><span>Critical Path</span></div>
                    <div style={styles.legendItem}><div style={{ ...styles.legendBox, backgroundColor: '#8b5cf6' }} /><span>Milestone</span></div>
                    <div style={styles.legendItem}><span>⚠️ Εξάρτηση Πελάτη</span></div>
                    <div style={styles.legendItem}><div style={{ ...styles.legendBox, backgroundColor: '#10b981' }} /><span>{L.legendCompleted}</span></div>
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: showCriticalOnly ? '#ef4444' : '#6b7280', cursor: 'pointer', userSelect: 'none' }}>
                      <input type="checkbox" checked={showCriticalOnly} onChange={e => setShowCriticalOnly(e.target.checked)} style={{ accentColor: '#ef4444', width: '14px', height: '14px' }} />
                      {L.checkCriticalOnly}
                    </label>
                    {scheduleData?.start_date && (() => {
                      if (todayDay < 0 || todayDay > totalDays) return null;
                      return (
                        <button
                          onClick={() => {
                            const containerWidth = ganttScrollRef.current?.offsetWidth || 800;
                            const x = todayDay * dayWidth - containerWidth / 2;
                            ganttScrollRef.current?.scrollTo({ left: Math.max(0, x), behavior: 'smooth' });
                          }}
                          style={{ fontSize: '12px', color: '#ef4444', background: 'none', border: '1px solid #fca5a5', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', fontWeight: '600' }}
                        >→ {L.btnToday}</button>
                      );
                    })()}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '2px 6px' }}>
                      <button onClick={zoomOut} disabled={dayWidth <= ZOOM_STEPS[0]} style={{ width: '24px', height: '24px', border: 'none', background: 'none', fontSize: '16px', cursor: dayWidth <= ZOOM_STEPS[0] ? 'not-allowed' : 'pointer', color: dayWidth <= ZOOM_STEPS[0] ? '#d1d5db' : '#374151', lineHeight: 1 }}>−</button>
                      <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '28px', textAlign: 'center' }}>{Math.round((dayWidth / DEFAULT_dayWidth) * 100)}%</span>
                      <button onClick={zoomIn} disabled={dayWidth >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} style={{ width: '24px', height: '24px', border: 'none', background: 'none', fontSize: '16px', cursor: dayWidth >= ZOOM_STEPS[ZOOM_STEPS.length - 1] ? 'not-allowed' : 'pointer', color: dayWidth >= ZOOM_STEPS[ZOOM_STEPS.length - 1] ? '#d1d5db' : '#374151', lineHeight: 1 }}>+</button>
                    </div>
                  </div>
                </div>

                {/* Gantt */}
                <div style={{ borderRadius: '8px', border: '1px solid #e5e7eb', width: '100%', overflow: 'hidden' }}>
                  <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    {/* Frozen header row: stays visible while the activities below scroll vertically */}
                    <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 20, backgroundColor: '#f9fafb' }}>
                      <div style={{ width: `${GANTT_LABEL_WIDTH}px`, flexShrink: 0, borderRight: '2px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                        <div style={{ height: showDays ? '72px' : '64px', display: 'flex', alignItems: 'flex-end', padding: '0 8px 8px', gap: 0 }}>
                          <span style={{ flex: 1, fontSize: '11px', fontWeight: '600', color: '#6b7280' }}>Activity / Milestone</span>
                          <span style={{ width: '68px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: '#6b7280' }}>Έναρξη</span>
                          <span style={{ width: '68px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: '#6b7280' }}>Λήξη</span>
                          <span style={{ width: '44px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: '#6b7280' }}>Δ.</span>
                        </div>
                      </div>
                      <div ref={ganttHeaderScrollRef} style={{ flex: 1, overflow: 'hidden', backgroundColor: '#f9fafb' }}>
                        <div style={{ height: '32px', position: 'relative', borderBottom: '1px solid #e5e7eb', width: `${ganttWidth}px` }}>
                          {monthMarkers.map((m, i) => (
                            <div key={i} style={{ position: 'absolute', left: `${m.day * dayWidth}px`, top: 0, bottom: 0, borderLeft: '1px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', width: `${m.widthDays * dayWidth}px`, overflow: 'hidden' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#374151', whiteSpace: 'nowrap' }}>{m.label}</span>
                            </div>
                          ))}
                          {(() => {
                            if (!scheduleData?.start_date || todayDay < 0) return null;
                            const isOverdue = todayDay > totalDays;
                            return (
                              <div style={{ position: 'absolute', left: `${todayDay * dayWidth}px`, top: 0, bottom: 0, width: '2px', backgroundColor: '#ef4444', zIndex: 15 }}>
                                <div style={{ position: 'absolute', top: '4px', left: '3px', backgroundColor: isOverdue ? '#6b7280' : '#ef4444', color: 'white', fontSize: '9px', fontWeight: '700', padding: '1px 4px', borderRadius: '3px', whiteSpace: 'nowrap' }}>
                                  {isOverdue ? '⚠️ Εκτός' : L.legendToday}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        {!showDays && (
                          <div style={{ height: '32px', position: 'relative', borderBottom: '2px solid #e5e7eb', width: `${ganttWidth}px` }}>
                            {weekMarkers.map((w, i) => (
                              <div key={i} style={{ position: 'absolute', left: `${w.day * dayWidth}px`, top: 0, bottom: 0, borderLeft: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', paddingLeft: '3px', width: `${dayWidth * 7}px` }}>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>{w.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {showDays && (
                          <div style={{ height: '40px', position: 'relative', borderBottom: '2px solid #e5e7eb', width: `${ganttWidth}px`, overflow: 'hidden' }}>
                            {dayMarkers.map((d, i) => (
                              <div key={i} style={{ position: 'absolute', left: `${d.day * dayWidth}px`, top: 0, bottom: 0, width: `${dayWidth}px`, backgroundColor: d.isWeekend ? '#fef3c7' : 'transparent', borderLeft: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1px' }}>
                                <span style={{ fontSize: '9px', color: d.isWeekend ? '#b45309' : '#374151', fontWeight: d.isWeekend ? '700' : '500', lineHeight: 1 }}>{d.label}</span>
                                <span style={{ fontSize: '8px', color: d.isWeekend ? '#b45309' : '#9ca3af', fontWeight: d.isWeekend ? '600' : '400', lineHeight: 1 }}>{d.initial}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  <div style={{ display: 'flex' }}>
                    {/* LEFT: sticky labels */}
                    {(() => {
                      const sd = scheduleData?.start_date || '';
                      const fmtDay = (day: number) => {
                        const iso = dayToDate(sd, day);
                        if (!iso) return '—';
                        const [, m, d] = iso.split('-');
                        return `${parseInt(d)}/${parseInt(m)}`;
                      };
                      const COL_START = 68; const COL_END = 68; const COL_DUR = 44;
                      const colStyle = (w: number): React.CSSProperties => ({ width: `${w}px`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' });
                      const numInputStyle: React.CSSProperties = { width: '100%', minWidth: 0, fontSize: '10px', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 2px', textAlign: 'center', color: '#374151', backgroundColor: '#f8fafc', fontWeight: '500', WebkitAppearance: 'none' as any, MozAppearance: 'textfield' as any, boxSizing: 'border-box' as const };
                      const dateCellStyle: React.CSSProperties = { fontSize: '10px', color: '#374151', fontWeight: '500', textAlign: 'center', padding: '2px 4px', borderRadius: '4px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', display: 'block', boxSizing: 'border-box' as const };
                      const DateCell = ({ day, field, itemId, isMilestone, minDay }: { day: number; field: string; itemId: string; isMilestone: boolean; minDay?: number }) => (
                        <label style={{ position: 'relative', display: 'block', cursor: canEdit ? 'pointer' : 'default', userSelect: 'none' }}>
                          <span style={dateCellStyle}>{fmtDay(day)}</span>
                          {canEdit && (!isMilestone || field === 'start_day') && (
                            <input type="date" value={dayToDate(sd, day)} min={minDay ? dayToDate(sd, minDay) : undefined}
                              onChange={e => handleInlineEdit(itemId, isMilestone, field, dateToDays(sd, e.target.value))}
                              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', padding: 0, margin: 0, border: 'none' }} />
                          )}
                        </label>
                      );
                      return (
                        <div style={{ width: `${GANTT_LABEL_WIDTH}px`, flexShrink: 0, borderRight: '2px solid #e5e7eb', backgroundColor: 'white', zIndex: 1 }}>
                          {phases.map(phase => {
                            const phaseItems = getPhaseItems(phase.id);
                            const isCollapsed = collapsedPhases.has(phase.id);
                            const phaseActs = phaseItems.filter(i => !i.is_milestone);
                            const phMinStart = phaseActs.length ? Math.min(...phaseActs.map(i => i.start_day)) : 0;
                            const phMaxEnd = phaseActs.length ? Math.max(...phaseActs.map(i => i.end_day)) : 0;
                            // Sum of each activity's own working-day span (MD total) — NOT the elapsed
                            // working-day span from the phase's earliest start to latest end, which would
                            // also count idle gap days between activities as if they were phase workload.
                            const phDur = phaseActs.reduce((sum, a) => sum + (projStart ? calToWork(a.start_day, a.end_day, projStart, resolvedCalendar) : (a.end_day - a.start_day + 1)), 0);
                            return (
                              <div key={phase.id}>
                                <div style={{ ...styles.phaseHeaderLabel, borderLeft: `4px solid ${phase.color}`, padding: '0 8px', gap: 0 }}>
                                  <button style={styles.phaseCollapseBtn} onClick={() => togglePhase(phase.id)}>{isCollapsed ? '▶' : '▼'}</button>
                                  {editingPhaseId === phase.id ? (
                                    <input autoFocus value={editingPhaseTitle} onChange={e => setEditingPhaseTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleRenamePhase(phase.id); else if (e.key === 'Escape') setEditingPhaseId(null); }} onBlur={() => handleRenamePhase(phase.id)} style={{ ...styles.phaseHeaderText, color: phase.color, background: 'transparent', border: 'none', borderBottom: `1px solid ${phase.color}`, outline: 'none', padding: '0 2px', flex: 1 }} />
                                  ) : (
                                    <span title={phase.title} style={{ ...styles.phaseHeaderText, color: phase.color, cursor: 'pointer', flex: 1 }} onDoubleClick={() => { setEditingPhaseId(phase.id); setEditingPhaseTitle(phase.title); }}>{phase.title}</span>
                                  )}
                                  <span style={{ ...colStyle(COL_START), fontSize: '11px', color: phase.color, fontWeight: '700' }}>{phaseActs.length ? fmtDay(phMinStart) : '—'}</span>
                                  <span style={{ ...colStyle(COL_END), fontSize: '11px', color: phase.color, fontWeight: '700' }}>{phaseActs.length ? fmtDay(phMaxEnd) : '—'}</span>
                                  <span style={{ ...colStyle(COL_DUR), fontSize: '11px', color: phase.color, fontWeight: '700' }}>{phaseActs.length ? `${phDur}d` : '—'}</span>
                                </div>
                                {!isCollapsed && phaseItems.map((item, i) => (
                                  <div key={item.id} style={{ ...styles.ganttRowLabel, backgroundColor: i % 2 === 0 ? '#fafafa' : 'white', paddingLeft: '24px', gap: 0 }}>
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden' }}>
                                      {item.is_milestone && <span style={{ color: '#8b5cf6', fontSize: '10px', flexShrink: 0 }}>◆</span>}
                                      {item.is_critical && !item.is_milestone && <span style={{ ...styles.criticalDot, flexShrink: 0 }} />}
                                      <span title={item.title} style={{ ...styles.ganttLabelText, fontWeight: item.is_milestone ? '600' : '400' }}>{dc(item)} {item.title}</span>
                                    </div>
                                    <div style={colStyle(COL_START)}><DateCell day={item.start_day} field="start_day" itemId={item.id} isMilestone={item.is_milestone} /></div>
                                    <div style={colStyle(COL_END)}><DateCell day={item.end_day} field="end_day" itemId={item.id} isMilestone={item.is_milestone} minDay={item.start_day} /></div>
                                    <div style={{ ...colStyle(COL_DUR), padding: '0 5px', boxSizing: 'border-box' as const }}>
                                      {canEdit && !item.is_milestone
                                        ? <input type="number" min="1" value={projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days} style={numInputStyle} onChange={e => handleInlineEdit(item.id, item.is_milestone, 'duration_days', parseInt(e.target.value) || 1)} />
                                        : <span style={{ fontSize: '11px', color: '#374151', fontWeight: '500', textAlign: 'center' as const, display: 'block', width: '100%' }}>{(projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days)}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                          {unassignedItems.length > 0 && (
                            <div>
                              <div style={{ ...styles.phaseHeaderLabel, borderLeft: '4px solid #6b7280' }}>
                                <span style={{ ...styles.phaseHeaderText, color: '#6b7280', flex: 1 }}>{L.defaultPhaseTitle}</span>
                              </div>
                              {unassignedItems.map((item, i) => (
                                <div key={item.id} style={{ ...styles.ganttRowLabel, backgroundColor: i % 2 === 0 ? '#fafafa' : 'white', paddingLeft: '24px', gap: 0 }}>
                                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden' }}>
                                    {item.is_milestone && <span style={{ color: '#8b5cf6', fontSize: '10px' }}>◆</span>}
                                    <span title={item.title} style={styles.ganttLabelText}>{dc(item)} {item.title}</span>
                                  </div>
                                  <div style={colStyle(COL_START)}><DateCell day={item.start_day} field="start_day" itemId={item.id} isMilestone={item.is_milestone} /></div>
                                  <div style={colStyle(COL_END)}><DateCell day={item.end_day} field="end_day" itemId={item.id} isMilestone={item.is_milestone} minDay={item.start_day} /></div>
                                  <div style={{ ...colStyle(COL_DUR), padding: '0 5px', boxSizing: 'border-box' as const }}>
                                    {canEdit && !item.is_milestone
                                      ? <input type="number" min="1" value={projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days} style={numInputStyle} onChange={e => handleInlineEdit(item.id, item.is_milestone, 'duration_days', parseInt(e.target.value) || 1)} />
                                      : <span style={{ fontSize: '11px', color: '#374151', fontWeight: '500', textAlign: 'center' as const, display: 'block', width: '100%' }}>{(projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days)}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* RIGHT: chart area */}
                    <div ref={ganttScrollRef} onMouseDown={handlePanStart}
                      onScroll={e => { if (ganttHeaderScrollRef.current) ganttHeaderScrollRef.current.scrollLeft = e.currentTarget.scrollLeft; }}
                      style={{ flex: 1, overflowX: 'auto', cursor: isPanning ? 'grabbing' : 'grab', userSelect: isPanning ? 'none' : undefined }}>
                    <div style={{ width: `${ganttWidth}px`, position: 'relative' }}>
                      <div style={{ position: 'relative', width: `${ganttWidth}px` }}>
                        {(() => {
                          if (!scheduleData?.start_date || todayDay < 0) return null;
                          const isOverdue = todayDay > totalDays;
                          return <div style={{ position: 'absolute', left: `${todayDay * dayWidth}px`, top: 0, bottom: 0, width: '2px', borderLeft: '2px dashed #ef4444', opacity: isOverdue ? 0.4 : 0.6, zIndex: 5, pointerEvents: 'none' }} />;
                        })()}

                        {/* ── Dependency arrows SVG overlay ── */}
                        {(() => {
                          const rowPos = buildRowPositions();
                          const itemMap: Record<string, Activity> = {};
                          allItems.forEach(a => { itemMap[a.id] = a; });

                          // Two colors: neutral for normal deps, red for critical path
                          const COLOR_NORMAL = '#94a3b8';
                          const COLOR_CRIT   = '#ef4444';

                          const buildDepPath = (x1: number, y1: number, x2: number, y2: number): string => {
                            const r = 5;
                            const sign = y2 >= y1 ? 1 : -1;
                            if (Math.abs(y2 - y1) < r * 2) return `M ${x1},${y1} L ${x2},${y2}`;

                            if (x2 >= x1 + r * 3) {
                              // Forward: exit right → vertical → enter from left (arrowhead →)
                              const bx = x1 + 14;
                              return [
                                `M ${x1},${y1}`,
                                `L ${bx - r},${y1}`,
                                `Q ${bx},${y1} ${bx},${y1 + sign * r}`,
                                `L ${bx},${y2 - sign * r}`,
                                `Q ${bx},${y2} ${bx + r},${y2}`,
                                `L ${x2},${y2}`,
                              ].join(' ');
                            } else {
                              // Backward/tight: 5-segment path — always enters x2 from the LEFT (arrowhead →)
                              const outX = x1 + 14;
                              const inX  = Math.min(x2 - 14, outX - r * 2);
                              const midY = (y1 + y2) / 2;
                              return [
                                `M ${x1},${y1}`,
                                `L ${outX - r},${y1}`, `Q ${outX},${y1} ${outX},${y1 + sign * r}`,
                                `L ${outX},${midY - sign * r}`, `Q ${outX},${midY} ${outX - r},${midY}`,
                                `L ${inX + r},${midY}`, `Q ${inX},${midY} ${inX},${midY + sign * r}`,
                                `L ${inX},${y2 - sign * r}`, `Q ${inX},${y2} ${inX + r},${y2}`,
                                `L ${x2},${y2}`,
                              ].join(' ');
                            }
                          };

                          const arrows: React.ReactNode[] = [];
                          allItems.forEach(item => {
                            normalizeDeps(item.dependencies).forEach(link => {
                              const pred = itemMap[link.id];
                              if (!pred || rowPos[pred.id] == null || rowPos[item.id] == null) return;

                              const y1 = rowPos[pred.id];
                              const y2 = rowPos[item.id];

                              let x1: number, x2: number;
                              switch (link.type) {
                                case 'SS': x1 = (pred.start_day - 1) * dayWidth; x2 = (item.start_day - 1) * dayWidth; break;
                                case 'FF': x1 = (pred.start_day + pred.duration_days - 1) * dayWidth; x2 = (item.start_day + item.duration_days - 1) * dayWidth; break;
                                case 'SF': x1 = (pred.start_day - 1) * dayWidth; x2 = (item.start_day + item.duration_days - 1) * dayWidth; break;
                                default:   x1 = (pred.start_day + pred.duration_days - 1) * dayWidth; x2 = (item.start_day - 1) * dayWidth;
                              }

                              const isCrit = pred.is_critical && item.is_critical;
                              const color = isCrit ? COLOR_CRIT : COLOR_NORMAL;
                              const markerId = isCrit ? 'arr-crit' : 'arr-normal';

                              arrows.push(
                                <path
                                  key={`${link.id}->${item.id}-${link.type}`}
                                  d={buildDepPath(x1, y1, x2, y2)}
                                  fill="none"
                                  stroke={color}
                                  strokeWidth="1"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  markerEnd={`url(#${markerId})`}
                                  opacity="0.5"
                                />,
                              );
                            });
                          });

                          if (!arrows.length) return null;

                          return (
                            <svg style={{ position: 'absolute', top: 0, left: 0, width: `${ganttWidth}px`, height: '100%', pointerEvents: 'none', zIndex: 7, overflow: 'visible' }}>
                              <defs>
                                <marker id="arr-normal" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
                                  <path d="M 0,0 L 5,2.5 L 0,5 Z" fill={COLOR_NORMAL} />
                                </marker>
                                <marker id="arr-crit" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
                                  <path d="M 0,0 L 5,2.5 L 0,5 Z" fill={COLOR_CRIT} />
                                </marker>
                              </defs>
                              {arrows}
                            </svg>
                          );
                        })()}

                        {phases.map(phase => {
                          const phaseItems = getPhaseItems(phase.id);
                          const isCollapsed = collapsedPhases.has(phase.id);
                          return (
                            <div key={phase.id}>
                              <div style={{ height: '36px', position: 'relative', backgroundColor: phase.color + '15', borderBottom: `1px solid ${phase.color}30`, borderTop: '1px solid #e5e7eb' }}>
                                {weekendBlocks.map((wb, j) => <div key={j} style={{ position: 'absolute', left: `${wb.day * dayWidth}px`, top: 0, bottom: 0, width: `${dayWidth}px`, backgroundImage: 'radial-gradient(circle, #b0b8c8 1px, transparent 1px)', backgroundSize: '4px 4px', pointerEvents: 'none', zIndex: 1 }} />)}
                                {weekMarkers.map((w, j) => <div key={j} style={{ position: 'absolute', left: `${w.day * dayWidth}px`, top: 0, bottom: 0, width: '1px', backgroundColor: '#f3f4f6' }} />)}
                                {monthMarkers.map((m, j) => <div key={j} style={{ position: 'absolute', left: `${m.day * dayWidth}px`, top: 0, bottom: 0, width: '1px', backgroundColor: '#e5e7eb' }} />)}
                              </div>
                              {!isCollapsed && phaseItems.map((item, i) => (
                                <div key={item.id} style={{ height: '40px', position: 'relative', borderBottom: '1px solid #f3f4f6', backgroundColor: i % 2 === 0 ? '#fafafa' : 'white' }}>
                                  {weekendBlocks.map((wb, j) => <div key={j} style={{ position: 'absolute', left: `${wb.day * dayWidth}px`, top: 0, bottom: 0, width: `${dayWidth}px`, backgroundImage: 'radial-gradient(circle, #b0b8c8 1px, transparent 1px)', backgroundSize: '4px 4px', pointerEvents: 'none', zIndex: 1 }} />)}
                                  {weekMarkers.map((w, j) => <div key={j} style={{ position: 'absolute', left: `${w.day * dayWidth}px`, top: 0, bottom: 0, width: '1px', backgroundColor: '#f3f4f6' }} />)}
                                  {monthMarkers.map((m, j) => <div key={j} style={{ position: 'absolute', left: `${m.day * dayWidth}px`, top: 0, bottom: 0, width: '1px', backgroundColor: '#e5e7eb' }} />)}
                                  {item.is_milestone ? (
                                    <div style={{ position: 'absolute', top: '12px', left: `${(item.start_day - 1) * dayWidth}px`, width: '16px', height: '16px', transform: 'rotate(45deg)', backgroundColor: phase.color, zIndex: 2, cursor: canEdit ? (dragging?.id === item.id ? 'grabbing' : 'grab') : 'pointer' }} title={`${item.title} ◆`} onMouseDown={e => handleDragStart(e, item)} onClick={() => { if (!wasDraggedRef.current) setViewingActivity(item); }} />
                                  ) : (
                                    <div
                                      style={{ position: 'absolute', top: '10px', left: `${(item.start_day - 1) * dayWidth}px`, width: `${Math.max(item.duration_days * dayWidth, 8)}px`, height: '20px', borderRadius: '4px', backgroundColor: getStatusColor(item), cursor: canEdit ? (resizing?.id === item.id ? 'ew-resize' : dragging?.id === item.id ? 'grabbing' : 'grab') : 'pointer', opacity: dragging?.id === item.id ? 0.8 : 1, display: 'flex', alignItems: 'center', paddingLeft: '6px', userSelect: 'none', zIndex: 2, overflow: 'visible' }}
                                      title={`${item.title} (${projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days}d) — ${item.progress_pct ?? 0}%`}
                                      onMouseDown={e => handleDragStart(e, item)}
                                      onMouseEnter={() => setHoveredBarId(item.id)}
                                      onMouseLeave={() => setHoveredBarId(prev => prev === item.id ? null : prev)}
                                      onClick={() => { if (!wasDraggedRef.current) setViewingActivity(item); }}
                                    >
                                      {/* Unfilled overlay — right portion shows remaining work */}
                                      {(item.progress_pct ?? 0) < 100 && (
                                        <div style={{ position: 'absolute', top: 0, left: `${item.progress_pct ?? 0}%`, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: (item.progress_pct ?? 0) === 0 ? '4px' : '0 4px 4px 0', pointerEvents: 'none', overflow: 'hidden' }} />
                                      )}
                                      <span style={{ position: 'relative', zIndex: 1, fontSize: '10px', color: 'white', overflow: 'hidden', whiteSpace: 'nowrap', pointerEvents: 'none', maxWidth: '100%' }}>
                                        {item.duration_days * dayWidth > 40 ? item.title.substring(0, 15) : ''}
                                      </span>
                                      {canEdit && hoveredBarId === item.id && item.duration_days * dayWidth > 16 && (
                                        <>
                                          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '8px', cursor: 'ew-resize', zIndex: 10, borderRadius: '4px 0 0 4px', backgroundColor: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            onMouseDown={e => handleResizeStart(e, item, 'left')} onClick={e => e.stopPropagation()}>
                                            <svg width="4" height="10" viewBox="0 0 4 10" fill="none"><line x1="1" y1="1" x2="1" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/><line x1="3" y1="1" x2="3" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                          </div>
                                          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '8px', cursor: 'ew-resize', zIndex: 10, borderRadius: '0 4px 4px 0', backgroundColor: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            onMouseDown={e => handleResizeStart(e, item, 'right')} onClick={e => e.stopPropagation()}>
                                            <svg width="4" height="10" viewBox="0 0 4 10" fill="none"><line x1="1" y1="1" x2="1" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/><line x1="3" y1="1" x2="3" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        })}

                        {unassignedItems.length > 0 && (
                          <div>
                            <div style={{ height: '36px', position: 'relative', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                              {weekendBlocks.map((wb, j) => <div key={j} style={{ position: 'absolute', left: `${wb.day * dayWidth}px`, top: 0, bottom: 0, width: `${dayWidth}px`, backgroundImage: 'radial-gradient(circle, #b0b8c8 1px, transparent 1px)', backgroundSize: '4px 4px', pointerEvents: 'none', zIndex: 1 }} />)}
                              {weekMarkers.map((w, j) => <div key={j} style={{ position: 'absolute', left: `${w.day * dayWidth}px`, top: 0, bottom: 0, width: '1px', backgroundColor: '#f3f4f6' }} />)}
                            </div>
                            {unassignedItems.map((item, i) => (
                              <div key={item.id} style={{ height: '40px', position: 'relative', borderBottom: '1px solid #f3f4f6', backgroundColor: i % 2 === 0 ? '#fafafa' : 'white' }}>
                                {weekendBlocks.map((wb, j) => <div key={j} style={{ position: 'absolute', left: `${wb.day * dayWidth}px`, top: 0, bottom: 0, width: `${dayWidth}px`, backgroundImage: 'radial-gradient(circle, #b0b8c8 1px, transparent 1px)', backgroundSize: '4px 4px', pointerEvents: 'none', zIndex: 1 }} />)}
                                {weekMarkers.map((w, j) => <div key={j} style={{ position: 'absolute', left: `${w.day * dayWidth}px`, top: 0, bottom: 0, width: '1px', backgroundColor: '#f3f4f6' }} />)}
                                {item.is_milestone ? (
                                  <div style={{ position: 'absolute', top: '12px', left: `${(item.start_day - 1) * dayWidth}px`, width: '16px', height: '16px', transform: 'rotate(45deg)', backgroundColor: '#6b7280', cursor: canEdit ? (dragging?.id === item.id ? 'grabbing' : 'grab') : 'pointer' }} title={`${item.title} ◆`} onMouseDown={e => handleDragStart(e, item)} onClick={() => { if (!wasDraggedRef.current) setViewingActivity(item); }} />
                                ) : (
                                  <div style={{ position: 'absolute', top: '10px', left: `${(item.start_day - 1) * dayWidth}px`, width: `${Math.max(item.duration_days * dayWidth, 8)}px`, height: '20px', borderRadius: '4px', backgroundColor: getStatusColor(item), cursor: canEdit ? (resizing?.id === item.id ? 'ew-resize' : 'grab') : 'pointer', zIndex: 2, overflow: 'visible' }}
                                    onMouseDown={e => handleDragStart(e, item)}
                                    onMouseEnter={() => setHoveredBarId(item.id)}
                                    onMouseLeave={() => setHoveredBarId(prev => prev === item.id ? null : prev)}
                                    onClick={() => { if (!wasDraggedRef.current) setViewingActivity(item); }}>
                                    {canEdit && hoveredBarId === item.id && item.duration_days * dayWidth > 16 && (
                                      <>
                                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '8px', cursor: 'ew-resize', zIndex: 10, borderRadius: '4px 0 0 4px', backgroundColor: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                          onMouseDown={e => handleResizeStart(e, item, 'left')} onClick={e => e.stopPropagation()}>
                                          <svg width="4" height="10" viewBox="0 0 4 10" fill="none"><line x1="1" y1="1" x2="1" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/><line x1="3" y1="1" x2="3" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                        </div>
                                        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '8px', cursor: 'ew-resize', zIndex: 10, borderRadius: '0 4px 4px 0', backgroundColor: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                          onMouseDown={e => handleResizeStart(e, item, 'right')} onClick={e => e.stopPropagation()}>
                                          <svg width="4" height="10" viewBox="0 0 4 10" fill="none"><line x1="1" y1="1" x2="1" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/><line x1="3" y1="1" x2="3" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'wbs' && (
              <div style={styles.section}>
                <div style={styles.wbsHeader}>
                  <h3 style={styles.sectionTitle}>Activity List</h3>
                  {canEdit && <p style={styles.wbsHint}>{L.hintInline}</p>}
                </div>

                {phases.map((phase) => {
                  const phaseItems = getPhaseItems(phase.id);
                  const isDraggingThis = dragPhaseId === phase.id;
                  const isDragOver = dragOverPhaseId === phase.id && dragPhaseId !== phase.id;
                  return (
                    <div
                      key={phase.id}
                      style={{ marginBottom: '24px', opacity: isDraggingThis ? 0.4 : 1, transition: 'opacity 0.15s' }}
                      onDragOver={e => { e.preventDefault(); setDragOverPhaseId(phase.id); }}
                      onDrop={() => handlePhaseDrop(phase.id)}
                      onDragLeave={() => setDragOverPhaseId(null)}
                    >
                      <div style={{ ...styles.wbsPhaseHeader, borderLeft: `4px solid ${phase.color}`, backgroundColor: phase.color + '10', outline: isDragOver ? `2px dashed ${phase.color}` : 'none', outlineOffset: '-2px' }}>
                        {canEdit && (
                          <span
                            draggable
                            onDragStart={() => setDragPhaseId(phase.id)}
                            onDragEnd={() => { setDragPhaseId(null); setDragOverPhaseId(null); }}
                            title={L.hintDrag}
                            style={{ cursor: 'grab', fontSize: '16px', color: '#9ca3af', marginRight: '8px', userSelect: 'none', lineHeight: 1 }}
                          >⠿</span>
                        )}
                        {canEdit && editingPhaseId === phase.id ? (
                          <input
                            autoFocus
                            value={editingPhaseTitle}
                            onChange={e => setEditingPhaseTitle(e.target.value)}
                            onBlur={() => handleRenamePhase(phase.id)}
                            onKeyDown={e => { if (e.key === 'Enter') handleRenamePhase(phase.id); if (e.key === 'Escape') setEditingPhaseId(null); }}
                            style={{ fontSize: '13px', fontWeight: '700', color: phase.color, border: 'none', borderBottom: `2px solid ${phase.color}`, outline: 'none', backgroundColor: 'transparent', padding: '0 4px', minWidth: '120px' }}
                          />
                        ) : (
                          <span
                            style={{ ...styles.wbsPhaseTitle, color: phase.color, cursor: canEdit ? 'text' : 'default' }}
                            onDoubleClick={() => { if (canEdit) { setEditingPhaseId(phase.id); setEditingPhaseTitle(phase.title); } }}
                            title={canEdit ? L.hintDoubleClick : undefined}
                          >{phase.title}</span>
                        )}
                        <span style={styles.wbsPhaseCount}>{(() => {
                          const acts = phaseItems.filter(i => !i.is_milestone);
                          // Sum of each activity's own working-day span (MD total), not the elapsed
                          // span from the phase's earliest start to latest end.
                          return acts.reduce((sum, a) => sum + (projStart ? calToWork(a.start_day, a.end_day, projStart, resolvedCalendar) : (a.end_day - a.start_day + 1)), 0);
                        })()} {L.summaryDays}</span>
                        {canEdit && (
                          <button style={{ ...styles.wbsPhaseDeleteBtn, marginLeft: 'auto' }} onClick={() => handleDeletePhase(phase.id)}>✕ Διαγραφή φάσης</button>
                        )}
                      </div>
                      <div style={styles.wbsTable}>
                        {phaseItems.length === 0 ? (
                          <div style={{ padding: '16px 20px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                            Δεν υπάρχουν activities σε αυτή τη φάση ακόμα. Πρόσθεσε μια νέα activity και επέλεξε αυτή τη φάση.
                          </div>
                        ) : (<>
                        <div style={styles.wbsTableHeader}>
                          <span style={{ width: '70px' }}>{L.listHeaders[0]}</span>
                          <span style={{ flex: 2 }}>{L.listHeaders[1]}</span>
                          <span style={{ width: '80px' }}>{L.listHeaders[2]}</span>
                          <span style={{ width: '95px' }}>{L.listHeaders[3]}</span>
                          <span style={{ width: '75px', textAlign: 'center' as const }}>{L.listHeaders[4]}</span>
                          <span style={{ width: '115px' }}>{L.listHeaders[5]}</span>
                          <span style={{ width: '80px', textAlign: 'center' as const, whiteSpace: 'nowrap' as const }}>{L.fieldProgress}</span>
                          <span style={{ flex: 1 }}>{L.listHeaders[6]}</span>
                          {canEdit && <span style={{ width: '72px' }}>Ενέργειες</span>}
                        </div>
                        {phaseItems.map((item, i) => (
                          <div key={item.id} style={{ ...styles.wbsRow, backgroundColor: (item as any).type === 'client_dependency' ? '#fffbeb' : i % 2 === 0 ? '#fafafa' : 'white', borderLeft: (item as any).type === 'client_dependency' ? '3px solid #f59e0b' : (showCriticalOnly && item.is_critical) ? '3px solid #ef4444' : item.is_milestone ? '3px solid #8b5cf6' : '3px solid transparent' }}>
                            <span style={{ width: '70px', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>{dc(item)}</span>
                            <span title={item.title} style={{ flex: 2, fontSize: '14px', fontWeight: item.is_milestone ? '600' : '400', color: (item as any).type === 'client_dependency' ? '#92400e' : '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.is_milestone && <span style={{ color: '#8b5cf6', marginRight: '6px' }}>◆</span>}{item.title}</span>
                            <span style={{ width: '80px' }}><span style={{ ...styles.typeBadge, backgroundColor: (item as any).type === 'client_dependency' ? '#fef3c7' : item.is_milestone ? '#ede9fe' : '#e0f2fe', color: (item as any).type === 'client_dependency' ? '#92400e' : item.is_milestone ? '#6d28d9' : '#0369a1' }}>{(item as any).type === 'client_dependency' ? '⚠️ Πελάτης' : item.is_milestone ? 'Milestone' : 'Activity'}</span></span>
                            <span style={{ width: '95px' }}>
                              {canEdit ? (
                                <label style={{ position: 'relative', display: 'block', cursor: 'pointer', userSelect: 'none' as const }}>
                                  <span style={styles.listDateChip}>{(() => { const iso = dayToDate(scheduleData?.start_date || '', item.start_day); if (!iso) return item.start_day; const [,m,d] = iso.split('-'); return `${parseInt(d)}/${parseInt(m)}`; })()}</span>
                                  <input type="date" value={dayToDate(scheduleData?.start_date || '', item.start_day)} onChange={e => handleInlineEdit(item.id, item.is_milestone, 'start_day', dateToDays(scheduleData?.start_date || '', e.target.value))} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', padding: 0, margin: 0, border: 'none' }} />
                                </label>
                              ) : (
                                <span style={styles.listDateChip}>{(() => { const iso = dayToDate(scheduleData?.start_date || '', item.start_day); if (!iso) return item.start_day; const [,m,d] = iso.split('-'); return `${parseInt(d)}/${parseInt(m)}`; })()}</span>
                              )}
                            </span>
                            <span style={{ width: '75px' }}>
                              {canEdit && !item.is_milestone
                                ? <input style={{ ...styles.inlineInput, WebkitAppearance: 'none' as any, MozAppearance: 'textfield' as any }} type="number" min="1" value={projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days} onChange={e => handleInlineEdit(item.id, item.is_milestone, 'duration_days', parseInt(e.target.value) || 1)} />
                                : <span style={styles.listDurChip}>{item.is_milestone ? '—' : (projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days)}</span>}
                            </span>
                            <span style={{ width: '115px' }}>{canEdit ? <select style={styles.statusSelect} value={item.status} onChange={e => handleStatusChange(item.id, item.is_milestone, e.target.value as Activity['status'])}><option value="not_started">{L.statusLabels['not_started']}</option><option value="in_progress">{L.statusLabels['in_progress']}</option><option value="completed">{L.statusLabels['completed']}</option><option value="delayed">{L.statusLabels['delayed']}</option></select> : <span style={{ fontSize: '12px', color: '#374151' }}>{getStatusLabel(item.status)}</span>}</span>
                            <span style={{ width: '80px', display: 'flex', justifyContent: 'center' as const }}>
                              <span style={{ fontSize: '12px', fontWeight: '600', color: item.progress_pct === 100 ? '#10b981' : item.progress_pct > 0 ? '#0ea5e9' : '#9ca3af', backgroundColor: item.progress_pct === 100 ? '#f0fdf4' : item.progress_pct > 0 ? '#f0f9ff' : '#f9fafb', border: `1px solid ${item.progress_pct === 100 ? '#bbf7d0' : item.progress_pct > 0 ? '#bae6fd' : '#e5e7eb'}`, padding: '3px 8px', borderRadius: '999px', whiteSpace: 'nowrap' as const }}>{item.progress_pct ?? 0}%</span>
                            </span>
                            <span style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{item.assigned_to.length > 0 ? item.assigned_to.map((name, j) => <span key={j} style={styles.assigneeBadge}>{name}</span>) : <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>}</span>
                            {canEdit && (
                              <span style={{ width: '72px', display: 'flex', gap: '4px' }}>
                                <button style={styles.wbsEditBtn} onClick={() => { openEditActivity(item); }}>✎</button>
                                <button style={styles.wbsDeleteBtn} onClick={() => handleDeleteActivity(item.id, item.is_milestone)}>✕</button>
                              </span>
                            )}
                          </div>
                        ))}
                        </>)}
                      </div>
                    </div>
                  );
                })}

                {unassignedItems.length > 0 && (
                  <div>
                    <div style={{ ...styles.wbsPhaseHeader, borderLeft: '4px solid #6b7280', backgroundColor: '#f9fafb' }}>
                      <span style={{ ...styles.wbsPhaseTitle, color: '#6b7280' }}>{L.defaultPhaseTitle}</span>
                    </div>
                    <div style={styles.wbsTable}>
                      {unassignedItems.map((item, i) => (
                        <div key={item.id} style={{ ...styles.wbsRow, backgroundColor: i % 2 === 0 ? '#fafafa' : 'white' }}>
                          <span style={{ width: '70px', fontSize: '12px', color: '#6b7280' }}>{dc(item)}</span>
                          <span title={item.title} style={{ flex: 2, fontSize: '14px', color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                          <span style={{ width: '80px' }}><span style={{ ...styles.typeBadge, backgroundColor: '#f3f4f6', color: '#6b7280' }}>{item.is_milestone ? 'Milestone' : 'Activity'}</span></span>
                          <span style={{ width: '95px' }}>
                            {canEdit ? (
                              <label style={{ position: 'relative', display: 'block', cursor: 'pointer', userSelect: 'none' as const }}>
                                <span style={styles.listDateChip}>{(() => { const iso = dayToDate(scheduleData?.start_date || '', item.start_day); if (!iso) return item.start_day; const [,m,d] = iso.split('-'); return `${parseInt(d)}/${parseInt(m)}`; })()}</span>
                                <input type="date" value={dayToDate(scheduleData?.start_date || '', item.start_day)} onChange={e => handleInlineEdit(item.id, item.is_milestone, 'start_day', dateToDays(scheduleData?.start_date || '', e.target.value))} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', padding: 0, margin: 0, border: 'none' }} />
                              </label>
                            ) : (
                              <span style={styles.listDateChip}>{(() => { const iso = dayToDate(scheduleData?.start_date || '', item.start_day); if (!iso) return item.start_day; const [,m,d] = iso.split('-'); return `${parseInt(d)}/${parseInt(m)}`; })()}</span>
                            )}
                          </span>
                          <span style={{ width: '75px' }}>
                            {canEdit && !item.is_milestone
                              ? <input style={{ ...styles.inlineInput, WebkitAppearance: 'none' as any, MozAppearance: 'textfield' as any }} type="number" min="1" value={projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days} onChange={e => handleInlineEdit(item.id, item.is_milestone, 'duration_days', parseInt(e.target.value) || 1)} />
                              : <span style={styles.listDurChip}>{item.is_milestone ? '—' : (projStart ? calToWork(item.start_day, item.end_day, projStart, resolvedCalendar) : item.duration_days)}</span>}
                          </span>
                          <span style={{ width: '115px' }}>{canEdit ? <select style={styles.statusSelect} value={item.status} onChange={e => handleStatusChange(item.id, item.is_milestone, e.target.value as Activity['status'])}><option value="not_started">{L.statusLabels['not_started']}</option><option value="in_progress">{L.statusLabels['in_progress']}</option><option value="completed">{L.statusLabels['completed']}</option><option value="delayed">{L.statusLabels['delayed']}</option></select> : <span style={{ fontSize: '12px', color: '#374151' }}>{getStatusLabel(item.status)}</span>}</span>
                          <span style={{ width: '60px', textAlign: 'center' as const }}>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: item.progress_pct === 100 ? '#10b981' : item.progress_pct > 0 ? '#0ea5e9' : '#9ca3af' }}>{item.progress_pct ?? 0}%</span>
                          </span>
                          <span style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{item.assigned_to.length > 0 ? item.assigned_to.map((n, j) => <span key={j} style={styles.assigneeBadge}>{n}</span>) : <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>}</span>
                          {canEdit && (
                            <span style={{ width: '72px', display: 'flex', gap: '4px' }}>
                              <button style={styles.wbsEditBtn} onClick={() => { openEditActivity(item); }}>✎</button>
                              <button style={styles.wbsDeleteBtn} onClick={() => handleDeleteActivity(item.id, item.is_milestone)}>✕</button>
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Phase Modal */}
      {showPhaseModal && canEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{L.phaseModalTitle}</h2>
            <div style={styles.field}>
              <label style={styles.label}>{L.phaseNameLabel}</label>
              <input style={styles.input} value={phaseForm.title} onChange={e => setPhaseForm({ ...phaseForm, title: e.target.value })} placeholder={L.phaseNamePlaceholder} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.phaseDescLabel}</label>
              <textarea style={{ ...styles.input, height: '60px', resize: 'none' }} value={phaseForm.description} onChange={e => setPhaseForm({ ...phaseForm, description: e.target.value })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.phasePositionLabel}</label>
              <select style={styles.select} value={phaseForm.insertAfter} onChange={e => setPhaseForm({ ...phaseForm, insertAfter: e.target.value })}>
                <option value="__start__">{L.phasePositionStart}</option>
                {[...scheduleData?.phases || []].sort((a, b) => a.order - b.order).map(p => (
                  <option key={p.id} value={p.id}>{L.phasePositionAfter(p.title)}</option>
                ))}
                <option value="__end__">{L.phasePositionEnd}</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.phaseColorLabel}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {phaseColors.map(color => (
                  <button key={color} style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: color, border: phaseForm.color === color ? '3px solid #1a1a2e' : '3px solid transparent', cursor: 'pointer' }} onClick={() => setPhaseForm({ ...phaseForm, color })} />
                ))}
              </div>
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={() => { setPhaseForm({ title: '', description: '', color: '#4f46e5', insertAfter: '__end__' }); setShowPhaseModal(false); }}>{C.cancel}</button>
              <button style={styles.saveBtn} onClick={handleSavePhase} disabled={saving}>{saving ? C.saving : C.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Info Popup */}
      {viewingActivity && (() => {
        const va = viewingActivity;
        const phase = scheduleData?.phases.find(p => p.id === va.phase_id);
        const statusLabels: Record<string, string> = L.statusLabels;
        const statusColors: Record<string, string> = { not_started: '#6b7280', in_progress: '#3b82f6', completed: '#10b981', delayed: '#ef4444' };
        const depTypeColors: Record<DepType, string> = { FS: '#6366f1', SS: '#0ea5e9', FF: '#10b981', SF: '#f59e0b' };
        const allItems = [...(scheduleData?.activities || []), ...(scheduleData?.milestones || [])];
        const actTeamEfforts = scopeActivityExtras[va.id]?.team_efforts || {};
        const teamsForDropdown = budgetTeams.filter(t => (actTeamEfforts[t] || 0) > 0).length > 0
          ? budgetTeams.filter(t => (actTeamEfforts[t] || 0) > 0)
          : budgetTeams;
        return (
          <div style={styles.overlay} onClick={() => setViewingActivity(null)}>
            <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px 32px', width: '100%', maxWidth: '720px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    {va.wbs_code && <span style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', fontFamily: 'monospace', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '999px' }}>{dc(va)}</span>}
                    {va.is_milestone && <span style={{ fontSize: '11px', fontWeight: '700', color: '#8b5cf6', backgroundColor: '#f5f3ff', padding: '2px 8px', borderRadius: '999px' }}>◆ Milestone</span>}
                    {va.is_critical && <span style={{ fontSize: '11px', fontWeight: '700', color: '#ef4444', backgroundColor: '#fef2f2', padding: '2px 8px', borderRadius: '999px' }}>! Critical</span>}
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e', margin: 0 }}>{va.title}</h2>
                  {phase && <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>📁 {phase.title}</div>}
                </div>
                <button onClick={() => setViewingActivity(null)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9ca3af', cursor: 'pointer', padding: '0', lineHeight: 1 }}>✕</button>
              </div>

              {/* Status + Progress */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: statusColors[va.status] || '#6b7280', backgroundColor: (statusColors[va.status] || '#6b7280') + '18', padding: '4px 12px', borderRadius: '999px' }}>{statusLabels[va.status] || va.status}</span>
                {!va.is_milestone && (
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>Πρόοδος</span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#374151' }}>{va.progress_pct ?? 0}%</span>
                    </div>
                    <div style={{ height: '6px', backgroundColor: '#f3f4f6', borderRadius: '999px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${va.progress_pct ?? 0}%`, backgroundColor: (va.progress_pct ?? 0) === 100 ? '#10b981' : '#6366f1', borderRadius: '999px', transition: 'width 0.3s' }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Timing */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                {[
                  { label: L.labelStartDay, value: `${L.daysAbbr} ${va.start_day}` },
                  { label: L.labelEndDay, value: `${L.daysAbbr} ${va.end_day}` },
                  { label: L.labelDuration, value: va.is_milestone ? '—' : `${projStart ? calToWork(va.start_day, va.end_day, projStart, resolvedCalendar) : va.duration_days} ${L.labelDays}` },
                ].map(({ label, value }) => (
                  <div key={label} style={{ backgroundColor: '#f9fafb', borderRadius: '10px', padding: '10px 14px' }}>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '3px' }}>{label}</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a2e' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Description */}
              {va.description && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{L.detailDescription}</div>
                  <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.6', backgroundColor: '#f9fafb', borderRadius: '8px', padding: '10px 14px' }}>{va.description}</p>
                </div>
              )}

              {/* Dependencies */}
              {va.dependencies.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>{L.detailDeps}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {va.dependencies.map(link => {
                      const dep = allItems.find(a => a.id === link.id);
                      if (!dep) return null;
                      const c = depTypeColors[link.type];
                      return (
                        <span key={link.id} style={{ fontSize: '12px', backgroundColor: c + '18', color: c, border: `1px solid ${c}40`, borderRadius: '8px', padding: '4px 10px', fontWeight: '600' }}>
                          <span style={{ fontFamily: 'monospace', marginRight: '4px' }}>{link.type}</span>
                          {dep.wbs_code && <span style={{ color: '#6b7280', marginRight: '4px' }}>{dc(dep)}</span>}
                          {dep.title}
                          {link.lag !== 0 && <span style={{ color: '#9ca3af', marginLeft: '4px' }}>{link.lag > 0 ? '+' : ''}{link.lag}d</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Assigned to */}
              {va.assigned_to.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>{L.detailAssign}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {va.assigned_to.map((name, i) => (
                      <span key={i} style={{ fontSize: '12px', backgroundColor: '#ede9fe', color: '#7c3aed', borderRadius: '999px', padding: '3px 10px', fontWeight: '600' }}>{name}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Timesheet — Actual MD entries */}
              {!va.is_milestone && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{L.detailAC}</div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>
                      {L.detailACTotal(parseFloat((va.time_entries || []).reduce((s, e) => s + e.md, 0).toFixed(1)))}
                    </div>
                  </div>

                  {/* Existing entries */}
                  {(va.time_entries || []).length > 0 && (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '8px' }}>
                      {[...(va.time_entries || [])].sort((a, b) => a.date.localeCompare(b.date)).map((entry, ei, arr) => (
                        <div key={entry.id} style={{ borderBottom: ei < arr.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                          {canEdit && editingEntry?.id === entry.id ? (
                            <div style={{ padding: '8px 12px', backgroundColor: '#f0f9ff', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '140px 70px 1fr 80px', gap: '6px', alignItems: 'center' }}>
                                <input type="date" value={editingEntry.date} onChange={e => setEditingEntry({ ...editingEntry, date: e.target.value })} style={{ fontSize: '12px', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: '6px', backgroundColor: 'white' }} />
                                <input type="number" min="0.5" step="0.5" value={editingEntry.md} onChange={e => setEditingEntry({ ...editingEntry, md: e.target.value })} style={{ fontSize: '12px', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: '6px', textAlign: 'center', backgroundColor: 'white' }} />
                                <select value={editingEntry.team} onChange={e => setEditingEntry({ ...editingEntry, team: e.target.value })} style={{ fontSize: '12px', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: '6px', backgroundColor: 'white' }}>
                                  <option value="">{L.timesheetTeamPlaceholder}</option>
                                  {teamsForDropdown.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <input type="number" min="0" max="100" step="1" value={editingEntry.progress} onChange={e => setEditingEntry({ ...editingEntry, progress: e.target.value })} style={{ fontSize: '12px', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: '6px', textAlign: 'center', backgroundColor: 'white' }} />
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '6px', alignItems: 'center' }}>
                                <input type="text" value={editingEntry.note} onChange={e => setEditingEntry({ ...editingEntry, note: e.target.value })} placeholder={L.timesheetNotePlaceholder} style={{ fontSize: '12px', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: '6px', backgroundColor: 'white' }} />
                                <button onClick={() => handleSaveEditEntry(va.id)} style={{ fontSize: '12px', padding: '5px 12px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>{C.save}</button>
                                <button onClick={() => setEditingEntry(null)} style={{ fontSize: '12px', padding: '5px 10px', backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>{C.cancel}</button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', fontSize: '13px' }}>
                              <span style={{ color: '#6b7280', minWidth: '90px' }}>{new Date(entry.date).toLocaleDateString(C.dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                              <span style={{ fontWeight: '700', color: '#4f46e5', minWidth: '50px' }}>{entry.md} MD</span>
                              {entry.team && <span style={{ fontSize: '11px', backgroundColor: '#ede9fe', color: '#4f46e5', borderRadius: '999px', padding: '2px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>{entry.team}</span>}
                              {entry.progress_pct !== undefined && <span style={{ fontSize: '11px', color: '#6b7280' }}>{entry.progress_pct}%</span>}
                              <span style={{ flex: 1, color: '#9ca3af', fontStyle: entry.note ? 'normal' : 'italic' }}>{entry.note || '—'}</span>
                              {canEdit && (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button onClick={() => setEditingEntry({ id: entry.id, date: entry.date, md: String(entry.md), team: entry.team || '', progress: String(entry.progress_pct ?? 0), note: entry.note || '' })} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '13px', padding: '0 3px', lineHeight: 1 }} title={C.editTip}>✎</button>
                                  <button onClick={() => handleDeleteTimeEntry(va.id, entry.id)} style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '13px', padding: '0 3px', lineHeight: 1 }} title={C.deleteTip}>✕</button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add entry form */}
                  {canEdit && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', backgroundColor: '#f9fafb', borderRadius: '10px', padding: '12px' }}>
                      {/* Row 1: Date | MD | Team | % */}
                      <div style={{ display: 'grid', gridTemplateColumns: '150px 80px 1fr 90px', gap: '8px', alignItems: 'center' }}>
                        <input type="date" value={tsDate} onChange={e => setTsDate(e.target.value)} style={{ fontSize: '12px', padding: '7px 8px', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', backgroundColor: 'white' }} />
                        <input type="number" min="0.5" step="0.5" value={tsMd} onChange={e => setTsMd(e.target.value)} placeholder="MD *" style={{ fontSize: '12px', padding: '7px 8px', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'center', backgroundColor: 'white' }} />
                        <select value={tsTeam} onChange={e => { setTsTeam(e.target.value); setTsError(''); }} style={{ fontSize: '12px', padding: '7px 8px', border: `1px solid ${tsError && !tsTeam ? '#ef4444' : '#e5e7eb'}`, borderRadius: '6px', color: tsTeam ? '#374151' : '#9ca3af', backgroundColor: 'white' }}>
                          <option value="">{L.timesheetTeamPlaceholder}</option>
                          {teamsForDropdown.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <input type="number" min={va.progress_pct ?? 0} max="100" step="1" value={tsProgress} onChange={e => { setTsProgress(e.target.value); setTsError(''); }} placeholder="% ολοκλ. *" style={{ fontSize: '12px', padding: '7px 8px', border: `1px solid ${tsError && tsProgress === '' ? '#ef4444' : '#e5e7eb'}`, borderRadius: '6px', textAlign: 'center', backgroundColor: 'white' }} />
                      </div>
                      {/* Row 2: Note | Button */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'center' }}>
                        <input type="text" value={tsNote} onChange={e => setTsNote(e.target.value)} placeholder={L.timesheetNotePlaceholder} style={{ fontSize: '12px', padding: '7px 8px', border: '1px solid #e5e7eb', borderRadius: '6px', backgroundColor: 'white' }} />
                        <button
                          onClick={() => handleAddTimeEntry(va.id, va.progress_pct ?? 0)}
                          disabled={!tsMd || parseFloat(tsMd) <= 0 || !tsTeam || tsProgress === ''}
                          style={{ padding: '7px 18px', backgroundColor: (parseFloat(tsMd) > 0 && tsTeam && tsProgress !== '') ? '#4f46e5' : '#e5e7eb', color: (parseFloat(tsMd) > 0 && tsTeam && tsProgress !== '') ? 'white' : '#9ca3af', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: (parseFloat(tsMd) > 0 && tsTeam && tsProgress !== '') ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}
                        >{L.timesheetAddBtn}</button>
                      </div>
                      {tsError && <div style={{ fontSize: '11px', color: '#ef4444' }}>⚠ {tsError}</div>}
                      {(va.progress_pct ?? 0) > 0 && <div style={{ fontSize: '11px', color: '#6b7280' }}>{L.detailProgressCurrent} <strong>{va.progress_pct}%</strong></div>}
                    </div>
                  )}
                </div>
              )}

              {/* Footer buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                {canEdit && (
                  <button
                    onClick={() => { setViewingActivity(null); openEditActivity(va); }}
                    style={{ flex: 1, padding: '10px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    ✎ {L.activityModalEditTitle}
                  </button>
                )}
                <button onClick={() => setViewingActivity(null)} style={{ flex: 1, padding: '10px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                  {C.cancel}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Activity Modal */}
      {showActivityModal && canEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h2 style={{ ...styles.modalTitle, margin: 0 }}>{editingActivity ? L.activityModalEditTitle : L.activityModalNewTitle}</h2>
              <button onClick={() => { setShowActivityModal(false); setEditingActivity(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '20px', lineHeight: 1, padding: '0 2px' }} title="Close">✕</button>
            </div>
            <div style={styles.field}>
                <label style={styles.label}>{L.fieldType}</label>
                <select style={styles.select} value={form.type || 'activity'} onChange={e => setForm({ ...form, type: e.target.value as 'activity' | 'milestone' | 'client_dependency', is_milestone: e.target.value === 'milestone', duration_days: e.target.value === 'milestone' ? 0 : form.duration_days })}>
                  <option value="activity">Activity</option>
                  <option value="milestone">Milestone</option>
                  <option value="client_dependency">⚠️ Εξάρτηση Πελάτη</option>
                </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldPhase}</label>
              <select style={styles.select} value={form.phase_id || ''} onChange={e => setForm({ ...form, phase_id: e.target.value })}>
                <option value="">{L.fieldPhasePlaceholder}</option>
                {scheduleData?.phases.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldTitle}</label>
              <input style={styles.input} value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldDescription}</label>
              <textarea style={{ ...styles.input, height: '48px', resize: 'none' }} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldStartDate}</label>
                <input style={styles.input} type="date" value={form._startDate || ''} onChange={e => {
                  const newDate = e.target.value;
                  let sd = scheduleData?.start_date || newDate;
                  let currentSchedule = scheduleData;
                  if (sd && newDate < sd) {
                    const shiftDays = Math.round((parseDate(sd).getTime() - parseDate(newDate).getTime()) / 86400000);
                    const shifted = {
                      ...scheduleData!,
                      start_date: newDate,
                      activities: (scheduleData?.activities || []).map(a => ({ ...a, start_day: (a.start_day || 1) + shiftDays, end_day: (a.end_day || 1) + shiftDays })),
                      milestones: (scheduleData?.milestones || []).map(m => ({ ...m, start_day: (m.start_day || 1) + shiftDays, end_day: (m.end_day || 1) + shiftDays })),
                    };
                    setScheduleData(shifted);
                    currentSchedule = shifted;
                    sd = newDate;
                  }
                  const startDay = dateToDays(sd, newDate);
                  const endDay = form._endDate ? Math.max(startDay, dateToDays(sd, form._endDate)) : startDay;
                  const ps3 = scheduleData?.start_date ? parseDate(scheduleData.start_date) : null;
                  setForm({ ...form, _startDate: newDate, _endDate: form._endDate || newDate, start_day: startDay, end_day: endDay, duration_days: ps3 ? calToWork(startDay, endDay, ps3, resolvedCalendar) : endDay - startDay + 1 });
                }} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldEndDate}</label>
                <input style={styles.input} type="date" value={form._endDate || ''} disabled={form.type === 'milestone'} min={form._startDate || ''} onChange={e => {
                  const sd = scheduleData?.start_date || '';
                  const startDay = form._startDate ? dateToDays(sd, form._startDate) : 1;
                  const endDay = dateToDays(sd, e.target.value);
                  const ps4 = scheduleData?.start_date ? parseDate(scheduleData.start_date) : null;
                  setForm({ ...form, _endDate: e.target.value, end_day: endDay, duration_days: Math.max(1, ps4 ? calToWork(startDay, endDay, ps4, resolvedCalendar) : endDay - startDay + 1) });
                }} />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldDuration}</label>
              <input style={styles.input} type="number" min="1" value={form.type === 'milestone' ? 0 : (form.duration_days || 0)} disabled={form.type === 'milestone'} onChange={e => {
                const dur = Math.max(1, parseInt(e.target.value) || 1);
                const sd = scheduleData?.start_date || '';
                const startDay = form.start_day || 1;
                const ps5 = sd ? parseDate(sd) : null;
                const endDay = ps5 ? addWorkingDays(startDay, dur, ps5, resolvedCalendar) : startDay + dur - 1;
                setForm({ ...form, duration_days: dur, end_day: endDay, _endDate: dayToDate(sd, endDay) });
              }} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldDeps}</label>
              <div style={{ border: '1px solid #ddd', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                {[...(scheduleData?.activities || []), ...(scheduleData?.milestones || [])]
                  .filter(a => a.id !== editingActivity?.id)
                  .sort((a, b) => a.start_day - b.start_day)
                  .length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#9ca3af', padding: '10px 12px', margin: 0 }}>{L.fieldDepsEmpty}</p>
                ) : (
                  [...(scheduleData?.activities || []), ...(scheduleData?.milestones || [])]
                    .filter(a => a.id !== editingActivity?.id)
                    .sort((a, b) => a.start_day - b.start_day)
                    .map(a => {
                      const deps = (form.dependencies || []) as DependencyLink[];
                      const existing = deps.find(d => d.id === a.id);
                      const checked = !!existing;
                      return (
                        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', backgroundColor: checked ? '#ede9fe' : 'transparent', borderBottom: '1px solid #f3f4f6' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => {
                              const newDeps: DependencyLink[] = e.target.checked
                                ? [...deps, { id: a.id, type: 'FS', lag: 0 }]
                                : deps.filter(d => d.id !== a.id);
                              setForm({ ...form, dependencies: newDeps });
                            }}
                          />
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', minWidth: '48px', fontFamily: 'monospace' }}>{dc(a)}</span>
                          <span style={{ flex: 1, fontSize: '13px', color: '#1a1a2e' }}>{a.title}{a.is_milestone && <span style={{ color: '#8b5cf6', marginLeft: '4px' }}>◆</span>}{a.is_critical && <span style={{ color: '#ef4444', marginLeft: '4px', fontWeight: '700' }}>!</span>}</span>
                          {checked && (
                            <>
                              <select
                                value={existing!.type}
                                onChange={e => setForm({ ...form, dependencies: deps.map(d => d.id === a.id ? { ...d, type: e.target.value as DepType } : d) })}
                                style={{ fontSize: '11px', padding: '2px 4px', borderRadius: '4px', border: '1px solid #c4b5fd', backgroundColor: '#f5f3ff', color: '#4f46e5', fontWeight: '700', cursor: 'pointer' }}
                              >
                                <option value="FS">FS</option>
                                <option value="SS">SS</option>
                                <option value="FF">FF</option>
                                <option value="SF">SF</option>
                              </select>
                              <input
                                type="number"
                                value={existing!.lag}
                                onChange={e => setForm({ ...form, dependencies: deps.map(d => d.id === a.id ? { ...d, lag: parseInt(e.target.value) || 0 } : d) })}
                                title="Lag (ημέρες)"
                                placeholder="lag"
                                style={{ width: '44px', fontSize: '11px', padding: '2px 4px', borderRadius: '4px', border: '1px solid #ddd', textAlign: 'center' }}
                              />
                              <span style={{ fontSize: '10px', color: '#9ca3af' }}>d</span>
                            </>
                          )}
                        </div>
                      );
                    })
                )}
              </div>
              {((form.dependencies || []) as DependencyLink[]).length > 0 && (
                <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {((form.dependencies || []) as DependencyLink[]).map(link => {
                    const dep = [...(scheduleData?.activities || []), ...(scheduleData?.milestones || [])].find(a => a.id === link.id);
                    if (!dep) return null;
                    const colors: Record<DepType, string> = { FS: '#6366f1', SS: '#0ea5e9', FF: '#10b981', SF: '#f59e0b' };
                    return (
                      <span key={link.id} style={{ fontSize: '11px', backgroundColor: colors[link.type] + '20', color: colors[link.type], border: `1px solid ${colors[link.type]}40`, borderRadius: '999px', padding: '2px 8px', fontWeight: '600' }}>
                        {link.type} · {dc(dep)} {dep.title}{link.lag !== 0 ? ` ${link.lag > 0 ? '+' : ''}${link.lag}d` : ''}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldAssign}</label>
              <div style={styles.assigneeList}>
                {orgMembers.length === 0 ? <p style={{ fontSize: '13px', color: '#9ca3af' }}>{L.fieldAssignEmpty}</p> : orgMembers.map(member => (
                  <label key={member.id} style={styles.assigneeCheck}>
                    <input type="checkbox" checked={(form.assigned_to || []).includes(member.name)} onChange={e => { const current = form.assigned_to || []; setForm({ ...form, assigned_to: e.target.checked ? [...current, member.name] : current.filter(n => n !== member.name) }); }} />
                    <span>{member.name} — {member.role}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldStatus}</label>
                <select style={styles.select} value={form.status || 'not_started'} onChange={e => {
                  const status = e.target.value as Activity['status'];
                  const autoPct = status === 'completed' ? 100 : status === 'not_started' ? 0 : (form.progress_pct ?? 0);
                  setForm({ ...form, status, progress_pct: autoPct });
                }}>
                  <option value="not_started">{L.statusLabels['not_started']}</option>
                  <option value="in_progress">{L.statusLabels['in_progress']}</option>
                  <option value="completed">{L.statusLabels['completed']}</option>
                  <option value="delayed">{L.statusLabels['delayed']}</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldProgress}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', fontSize: '14px', color: form.progress_pct > 0 ? '#1a1a2e' : '#9ca3af' }}>
                  <span style={{ fontWeight: '600' }}>{form.progress_pct ?? 0}%</span>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>{L.fieldProgressHelper}</span>
                </div>
              </div>
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={resetForm}>{C.cancel}</button>
              <button style={styles.saveBtn} onClick={handleSaveActivity} disabled={saving}>{saving ? C.saving : C.save}</button>
            </div>
          </div>
        </div>
      )}

      {WORKING_CALENDAR_ENABLED && (
        <WorkingCalendarModal
          project={project}
          token={token}
          isOpen={showWorkingCalendarModal}
          onClose={() => setShowWorkingCalendarModal(false)}
          scheduleData={scheduleData}
          initialTab={workingCalendarInitialTab}
          onSaved={({ calendarId, calendarName, calendarChanged, rescheduledSchedule, newEndDate }) => {
            if (calendarChanged) onProjectUpdate?.({ ...project, calendar_id: calendarId });
            // Any save (even just toggling a holiday or editing exceptions on the
            // calendar that's already the project's) can change what counts as a working
            // day — force the live resolved-calendar to refetch rather than keep serving
            // whatever it had resolved before this edit.
            setCalendarRefreshKey(k => k + 1);
            if (rescheduledSchedule) {
              setScheduleData(rescheduledSchedule);
              setCalendarToast(M.workingCalendar.toastSavedRescheduled(newEndDate ? new Date(newEndDate).toLocaleDateString(C.dateLocale) : ''));
            } else {
              setCalendarToast(M.workingCalendar.toastSaved);
            }
            setTimeout(() => setCalendarToast(''), 4000);
            fetchData();
          }}
        />
      )}

      {calendarToast && <div style={styles.calendarToast}>{calendarToast}</div>}

    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  userRole: { backgroundColor: '#4f46e5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breadcrumb: { padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky' as const, top: 64, zIndex: 99 },
  breadcrumbLink: { color: '#4f46e5', cursor: 'pointer', fontWeight: '500' },
  breadcrumbSep: { color: '#9ca3af', margin: '0 8px' },
  breadcrumbCurrent: { color: '#374151', fontWeight: '500' },
  main: { padding: '32px 48px', width: '100%', boxSizing: 'border-box' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  pageTitle: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  titleButtons: { display: 'flex', gap: '12px' },
  exportBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  calendarBtn: { backgroundColor: '#fff', color: '#374151', border: '1px solid #e5e7eb', padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  calendarDropdown: { position: 'absolute' as const, top: 'calc(100% + 6px)', left: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', width: '240px', padding: '8px', zIndex: 999 },
  calendarDropdownTitle: { fontSize: '10px', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.05em', padding: '4px 8px' },
  calendarDropdownItem: { fontSize: '13px', color: '#374151', padding: '8px', borderRadius: '6px', cursor: 'pointer' },
  calendarDropdownItemActive: { fontWeight: '700', color: '#4338ca', backgroundColor: '#eef2ff' },
  calendarDropdownEdit: { fontSize: '12px', fontWeight: '700', color: '#4338ca', padding: '8px', borderRadius: '6px', cursor: 'pointer', textAlign: 'center' as const, borderTop: '1px solid #f3f4f6', marginTop: '4px' },
  calendarKpiClickable: { cursor: 'pointer' },
  calendarToast: { position: 'fixed' as const, bottom: '24px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#1f2937', color: '#fff', padding: '10px 18px', borderRadius: '10px', fontSize: '13px', zIndex: 3000, boxShadow: '0 10px 30px rgba(0,0,0,0.25)' },
  phaseBtn: { backgroundColor: '#f59e0b', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  addBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  tabWrapper: { display: 'flex', justifyContent: 'center', marginBottom: '24px' },
  tabContainer: { display: 'inline-flex', backgroundColor: 'white', borderRadius: '999px', padding: '4px', border: '1px solid #e5e7eb', gap: '2px' },
  tab: { padding: '10px 28px', borderRadius: '999px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
  tabActive: { backgroundColor: '#1a1a2e', color: 'white' },
  tabInactive: { backgroundColor: 'transparent', color: '#6b7280' },
  loading: { color: '#666' },
  emptyState: { textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '12px', color: '#666' },
  section: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  summaryRow: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  summaryCard: { backgroundColor: '#f9fafb', borderRadius: '10px', padding: '12px 20px', textAlign: 'center', flex: 1, minWidth: '80px' },
  summaryValue: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
  summaryLabel: { fontSize: '11px', color: '#9ca3af', marginTop: '4px' },
  ganttLegend: { display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' },
  legendBox: { width: '16px', height: '10px', borderRadius: '3px' },
  phaseHeaderLabel: { height: '36px', display: 'flex', alignItems: 'center', padding: '0 8px', gap: '8px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', borderTop: '1px solid #e5e7eb' },
  phaseCollapseBtn: { backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '10px', color: '#6b7280', padding: '2px 4px' },
  phaseHeaderText: { fontSize: '12px', fontWeight: '700', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  phaseCount: { fontSize: '11px', color: '#6b7280', fontWeight: '700' },
  ganttRowLabel: { height: '40px', display: 'flex', alignItems: 'center', padding: '0 8px', borderBottom: '1px solid #f3f4f6' },
  ganttLabelContent: { display: 'flex', alignItems: 'center', gap: '6px', width: '100%' },
  ganttLabelText: { fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  criticalDot: { width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0 },
  wbsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  wbsHint: { fontSize: '12px', color: '#9ca3af', margin: 0 },
  wbsPhaseHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '8px 8px 0 0' },
  wbsPhaseTitle: { fontSize: '14px', fontWeight: '700' },
  wbsPhaseCount: { fontSize: '12px', color: '#6b7280', fontWeight: '700' },
  wbsPhaseDeleteBtn: { marginLeft: 'auto', backgroundColor: 'transparent', border: '1px solid #fee2e2', color: '#ef4444', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' },
  wbsTable: { borderRadius: '0 0 8px 8px', overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: '0' },
  wbsTableHeader: { display: 'flex', alignItems: 'center', padding: '10px 20px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', gap: '16px' },
  wbsRow: { display: 'flex', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #f3f4f6', gap: '16px' },
  typeBadge: { padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '500' },
  inlineInput: { width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', outline: 'none', backgroundColor: '#f8fafc', textAlign: 'center', color: '#374151', fontWeight: '500', boxSizing: 'border-box' as const },
  listDateChip: { display: 'block', fontSize: '12px', color: '#374151', fontWeight: '500', textAlign: 'center', padding: '4px 6px', borderRadius: '6px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', boxSizing: 'border-box' as const },
  listDurChip: { display: 'block', fontSize: '12px', color: '#374151', fontWeight: '500', textAlign: 'center', padding: '4px 6px', borderRadius: '6px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', boxSizing: 'border-box' as const },
  statusSelect: { fontSize: '12px', padding: '4px 6px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#f8fafc', cursor: 'pointer', width: '100%', color: '#374151' },
  assigneeBadge: { backgroundColor: '#ede9fe', color: '#4f46e5', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '500' },
  wbsEditBtn: { backgroundColor: '#ede9fe', color: '#4f46e5', border: 'none', width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  wbsDeleteBtn: { backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '680px', maxHeight: '70vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '17px', fontWeight: '600', color: '#1a1a2e', marginBottom: '16px' },
  field: { marginBottom: '10px' },
  label: { display: 'block', marginBottom: '4px', fontWeight: '500', color: '#333', fontSize: '13px' },
  input: { width: '100%', padding: '7px 10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  select: { width: '100%', padding: '7px 10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', outline: 'none', backgroundColor: 'white' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  assigneeList: { display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '120px', overflowY: 'auto', padding: '6px', border: '1px solid #ddd', borderRadius: '8px' },
  assigneeCheck: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '10px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default Schedule;
